import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grow,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Popper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import {
  Send as SendIcon,
  Mic as MicIcon,
  Close as CloseIcon,
  PostAdd as PostAddIcon,
  DeleteOutline as DeleteOutlineIcon,
  CreateNewFolder as CreateNewFolderIcon,
  AltRoute as AltRouteIcon,
  DataObject as DataObjectIcon,
  ViewList as ViewListIcon,
  Edit as EditIcon,
  DriveFileMoveOutlined as DriveFileMoveOutlinedIcon,
  FolderDeleteOutlined as FolderDeleteOutlinedIcon,
  FileCopyOutlined as FileCopyOutlinedIcon,
  DriveFileRenameOutline as DriveFileRenameOutlineIcon,
  NoteAddOutlined as NoteAddOutlinedIcon,
  EditNoteOutlined as EditNoteOutlinedIcon,
  SubtitlesOutlined as SubtitlesOutlinedIcon,
  AutoAwesomeOutlined as AutoAwesomeOutlinedIcon,
  FolderOpen as FolderOpenIcon,
  TabUnselected as TabUnselectedIcon,
  FormatIndentIncrease as FormatIndentIncreaseIcon,
  FormatIndentDecrease as FormatIndentDecreaseIcon,
  FormatListBulleted as FormatListBulletedIcon,
  KeyboardReturn as KeyboardReturnIcon,
  SubdirectoryArrowRight as SubdirectoryArrowRightIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import { alpha, styled } from '@mui/material/styles';
import { validateBulkContentLinesUniqueness } from '../../utils/bulkContentPasteParse';
import api from '../../services/api';
import {
  hoistOutlineRootsForNewDocument,
  parseUnifiedTreeImport,
  resolveNewDocumentImportNaming,
  shouldUseApplyOutlineOnNewDocument,
  validateOutlineForProposal,
  validateOutlineMinTopLevelForContentBucketAnchor,
} from '../../utils/outlineBulletParse';
import { useHelpContent } from '../../utils/HelpUtils';
import { DropdownFieldTooltip } from '../DropdownFieldTooltip';
import { invertSubcaseSelection } from '../../utils/CategoryUtils';
import { InvertSelectionButton } from '../category/CategoryContent';
import TreeAssistantIcon from '../icons/TreeAssistantIcon';
import type { Category } from '../../types/Category';

function findCategoryByIdInTree(nodes: Category[] | undefined | null, id: string): Category | null {
  if (!nodes?.length || !id.trim()) return null;
  for (const n of nodes) {
    if (n.id === id) return n;
    const inner = findCategoryByIdInTree(n.children, id);
    if (inner) return inner;
  }
  return null;
}

/** True for a content row bucket under the document (not the document root, which has no parent). */
function isContentCategoryBucketForOutline(cat: Category | null): boolean {
  return Boolean(cat?.contentCategory && cat.parentId != null && String(cat.parentId).trim() !== '');
}

/** Must match indent step in outlineBulletParse (2 space columns ≈ one tree level). */
const OUTLINE_EDITOR_LEVEL_SPACES = 2;

/** Import tab: max plain-text file size when loading from disk. */
const IMPORT_TEXT_FILE_MAX_BYTES = 2 * 1024 * 1024;

/** Leading markers stripped / detected for toolbar bullet toggle (subset of loose-outline bullets). */
const OUTLINE_EDITOR_BULLET_PREFIX = /^([*+\-–—•]\s*)+/;

/** Same ordered-marker rule as outlineBulletParse (smart newline / sibling arrows skip these). */
const OUTLINE_ENTER_ORDERED_PREFIX = /^(?:(?:\d{1,4}|[a-zA-Z]{1,4})[.)]\s+)/;
/** Loose bullets like outlineBulletParse LOOSE_BULLET_PREFIX. */
const OUTLINE_ENTER_LOOSE_BULLET_PREFIX =
  /^(?:[*+\-–—•‣›▸►▪▫□◻◇○>\[\]()\u2022-\u2025\u2043\u2219\u25AA-\u25AE\u25CB\u25CC\u25CF\u25E6→←↓↑◆◇★☆✓✗|\u2610\u2611]\s*)+/u;

function outlineLooseBulletAfterLead(afterIndent: string): boolean {
  if (OUTLINE_ENTER_ORDERED_PREFIX.test(afterIndent)) return false;
  return OUTLINE_ENTER_LOOSE_BULLET_PREFIX.test(afterIndent);
}

function outlineLineIndexAtOffset(text: string, offset: number): number {
  const o = Math.max(0, Math.min(offset, text.length));
  return text.slice(0, o).split('\n').length - 1;
}

/**
 * After a double-click, browsers often select "- word" as one token. If the selection starts inside the
 * line's leading bullet prefix, collapse the caret to just after the prefix so the title word is not selected.
 */
function outlineCollapseDoubleClickIfBulletPrefix(ta: HTMLTextAreaElement) {
  const text = ta.value;
  const a = ta.selectionStart;
  const b = ta.selectionEnd;
  if (a === b) return;
  const s = Math.min(a, b);
  const lineStart = text.lastIndexOf('\n', Math.max(0, s - 1)) + 1;
  const lineEndIdx = text.indexOf('\n', lineStart);
  const lineEnd = lineEndIdx === -1 ? text.length : lineEndIdx;
  const line = text.slice(lineStart, lineEnd);
  const leadM = /^[\t ]*/.exec(line);
  const leadLen = leadM ? leadM[0].length : 0;
  const afterLead = line.slice(leadLen);
  const stripped = afterLead.replace(OUTLINE_EDITOR_BULLET_PREFIX, '');
  const bulletPrefixLen = afterLead.length - stripped.length;
  if (bulletPrefixLen === 0) return;
  const absBulletStart = lineStart + leadLen;
  const absBulletEnd = absBulletStart + bulletPrefixLen;
  if (s >= absBulletStart && s < absBulletEnd) {
    ta.setSelectionRange(absBulletEnd, absBulletEnd);
  }
}

function outlineApplyBlockOp(
  text: string,
  selStart: number,
  selEnd: number,
  op: 'indent' | 'outdent' | 'bullet',
): { text: string; selStart: number; selEnd: number } {
  const lines = text.split('\n');
  const a = Math.min(selStart, selEnd);
  const b = Math.max(selStart, selEnd);
  const startLine = outlineLineIndexAtOffset(text, a);
  const endLine = outlineLineIndexAtOffset(text, b);
  const newLines = lines.slice();

  for (let i = startLine; i <= endLine; i++) {
    const line = newLines[i];
    const leadMatch = /^[\t ]*/.exec(line);
    const lead = leadMatch ? leadMatch[0] : '';
    const rest = line.slice(lead.length);
    if (op === 'indent') {
      newLines[i] = `${' '.repeat(OUTLINE_EDITOR_LEVEL_SPACES)}${line}`;
    } else if (op === 'outdent') {
      if (
        lead.length >= OUTLINE_EDITOR_LEVEL_SPACES &&
        lead.slice(0, OUTLINE_EDITOR_LEVEL_SPACES).split('').every((ch) => ch === ' ')
      ) {
        newLines[i] = lead.slice(OUTLINE_EDITOR_LEVEL_SPACES) + rest;
      } else if (lead.startsWith('\t')) {
        newLines[i] = lead.slice(1) + rest;
      } else if (lead.startsWith(' ')) {
        newLines[i] = lead.slice(1) + rest;
      }
    } else if (op === 'bullet') {
      const stripped = rest.replace(OUTLINE_EDITOR_BULLET_PREFIX, '');
      if (stripped.length < rest.length) {
        newLines[i] = `${lead}${stripped}`;
      } else {
        newLines[i] = `${lead}- ${rest}`;
      }
    }
  }

  const newText = newLines.join('\n');
  let pos = 0;
  for (let j = 0; j < startLine; j++) {
    pos += newLines[j].length + 1;
  }
  const newStart = pos;
  for (let j = startLine; j < endLine; j++) {
    pos += newLines[j].length + 1;
  }
  pos += newLines[endLine].length;
  const newEnd = pos;
  return { text: newText, selStart: newStart, selEnd: newEnd };
}

/** Line index containing the caret, or the line containing the end of a range (for post-indent caret). */
function outlineFocusLineForBlockSelection(selStart: number, selEnd: number, text: string): number {
  return outlineLineIndexAtOffset(text, selStart === selEnd ? selStart : Math.max(selStart, selEnd));
}

/** Offset of the character just after the last character of {@code lineIndex} (end of line, before `\n` if any). */
function outlineEndOfLineOffset(text: string, lineIndex: number): number {
  const lines = text.split('\n');
  if (lineIndex < 0) return 0;
  if (lineIndex >= lines.length) return text.length;
  let p = 0;
  for (let j = 0; j < lineIndex; j++) {
    p += lines[j].length + 1;
  }
  return p + lines[lineIndex].length;
}

function outlineInsertIndentedNewLine(text: string, caret: number, mode: 'sibling' | 'child'): { text: string; caret: number } {
  const c = Math.max(0, Math.min(caret, text.length));
  const lineStart = text.lastIndexOf('\n', c - 1) + 1;
  const lineEndIdx = text.indexOf('\n', lineStart);
  const lineEnd = lineEndIdx === -1 ? text.length : lineEndIdx;
  const line = text.slice(lineStart, lineEnd);
  const lead = (/^[\t ]*/.exec(line) ?? [''])[0];
  const deeper = mode === 'child' ? ' '.repeat(OUTLINE_EDITOR_LEVEL_SPACES) : '';
  const afterIndent = line.slice(lead.length);
  const bullet = outlineLooseBulletAfterLead(afterIndent) ? '- ' : '';
  const insertion = `\n${lead}${deeper}${bullet}`;
  const newText = text.slice(0, c) + insertion + text.slice(c);
  return { text: newText, caret: c + insertion.length };
}

/**
 * When pressing Enter at the end of a loose-bullet line, insert a newline with the same indent and a "- " bullet.
 * Returns null to let the browser handle Enter (e.g. mid-line split, ordered lists, Shift+Enter).
 */
function outlineEnterInsertsSiblingBullet(text: string, caret: number): { text: string; caret: number } | null {
  const c = Math.max(0, Math.min(caret, text.length));
  const lineStart = text.lastIndexOf('\n', c - 1) + 1;
  const lineEndIdx = text.indexOf('\n', lineStart);
  const lineEnd = lineEndIdx === -1 ? text.length : lineEndIdx;
  if (text.slice(c, lineEnd).trim().length > 0) {
    return null;
  }
  const line = text.slice(lineStart, lineEnd);
  const indentMatch = /^[\t ]*/.exec(line);
  const indent = indentMatch ? indentMatch[0] : '';
  const afterIndent = line.slice(indent.length);
  if (!outlineLooseBulletAfterLead(afterIndent)) {
    return null;
  }
  const insertion = `\n${indent}- `;
  return { text: text.slice(0, c) + insertion + text.slice(c), caret: c + insertion.length };
}

/**
 * At end of a line that starts with spaces/tabs, Enter inserts a newline plus the same leading whitespace
 * (same as the sibling arrow without a bullet). Skipped when {@link outlineEnterInsertsSiblingBullet} applies.
 */
function outlineEnterInsertsIndentedContinuation(text: string, caret: number): { text: string; caret: number } | null {
  const c = Math.max(0, Math.min(caret, text.length));
  const lineStart = text.lastIndexOf('\n', c - 1) + 1;
  const lineEndIdx = text.indexOf('\n', lineStart);
  const lineEnd = lineEndIdx === -1 ? text.length : lineEndIdx;
  if (text.slice(c, lineEnd).trim().length > 0) {
    return null;
  }
  const line = text.slice(lineStart, lineEnd);
  const leadMatch = /^[\t ]*/.exec(line);
  const lead = leadMatch ? leadMatch[0] : '';
  if (!lead) return null;
  const insertion = `\n${lead}`;
  return { text: text.slice(0, c) + insertion + text.slice(c), caret: c + insertion.length };
}

/** Same glyph as {@link TreeAssistantIcon}; supports older JSX that still says {@code AccountTreeIcon}. */
const AccountTreeIcon = TreeAssistantIcon;

/** After "document …" / "doc …", suggest library names */
const DOCUMENT_NAME_TRIGGER = /(?:^|[\s\n])(document|docs?)\s+(\S*)$/i;
/** After "category …" / "cat …", suggest category ids — list shows the name only, or Name (Parent) when that name repeats in the tree. */
const CATEGORY_TRIGGER = /(?:^|[\s\n])(category|categories|categoryId|cat)\s+(\S*)$/i;
/** After "rename category …" / "rename cat …", suggest category ids (same picker as category). */
const RENAME_CATEGORY_TRIGGER = /(?:^|[\s\n])rename\s+(?:category|categories|cat)\s+(\S*)$/i;
/** After "subcase …" / "contentSubcase …", suggest labels from the open document's subcase list. */
const SUBCASE_TRIGGER = /(?:^|[\s\n])(?:contentSubcase|subcase)\s+(\S*)$/i;

const MAX_CHAT_SUGGESTIONS = 20;

type FlatCategoryRow = {
  id: string;
  name: string;
  /** Immediate parent folder name; null only for roots of the subtree we pass from MainLayout (top tier under document root). */
  parentName: string | null;
  /** Full slash path for matching search fragments (folders + names). */
  pathLabel: string;
};

function flattenCategoriesForAutocomplete(nodes: Category[] | undefined, parentName: string | null = null, prefix = ''): FlatCategoryRow[] {
  if (!nodes?.length) return [];
  const out: FlatCategoryRow[] = [];
  for (const n of nodes) {
    const pathLabel = prefix ? `${prefix} / ${n.name}` : n.name;
    out.push({ id: n.id, name: n.name, parentName, pathLabel });
    if (n.children?.length) {
      out.push(...flattenCategoriesForAutocomplete(n.children, n.name, pathLabel));
    }
  }
  return out;
}

function normalizedCategoryAutocompleteName(name: string): string {
  return name.trim().toLowerCase();
}

function countNormalizedNamesAcrossTree(rows: readonly FlatCategoryRow[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const r of rows) {
    const k = normalizedCategoryAutocompleteName(r.name);
    m.set(k, (m.get(k) ?? 0) + 1);
  }
  return m;
}

function categoryPickDisplayLabel(row: FlatCategoryRow, globalNameDup: Map<string, number>): string {
  const k = normalizedCategoryAutocompleteName(row.name);
  if ((globalNameDup.get(k) ?? 0) <= 1) {
    return row.name;
  }
  if (row.parentName?.trim()) {
    return `${row.name} (${row.parentName})`;
  }
  return `${row.name} (document root)`;
}

/** Duplicate names under same parent are rare — add a shortened id suffix. */
function disambiguateCollidingPickLabels(rows: FlatCategoryRow[], labels: string[]): string[] {
  const seen = new Map<string, number>();
  return labels.map((label, i) => {
    const n = seen.get(label) ?? 0;
    seen.set(label, n + 1);
    if (n === 0) return label;
    const tail = shrinkCategoryIdForPickLabelSuffix(rows[i].id);
    return `${label} — ${tail}`;
  });
}

function shrinkCategoryIdForPickLabelSuffix(id: string): string {
  const t = id.trim();
  if (!t) return '?';
  const parts = t.split(':').filter((p) => p.length > 0);
  if (parts.length <= 4) return t.length <= 44 ? t : t.slice(-44);
  return `…${parts.slice(-3).join(':')}`;
}

/** Stronger ranks sort first — index 0 is the default highlight as the typed query narrows matches. */
function autocompleteMatchRank(haystack: string, ql: string): number {
  if (!ql.trim()) return 0;
  const d = haystack.trim().toLowerCase();
  const q = ql.trim().toLowerCase();
  if (!d || !q) return -1;
  if (d === q) return 350_000;
  const pos = d.indexOf(q);
  if (pos < 0) return -1;
  let rank = q.length * 100 + 1_000;
  if (pos === 0) rank += 18_000;
  rank -= pos * 80;
  return rank;
}

/**
 * Queries without ":" match only each category's own {@link FlatCategoryRow#name}. Parent names and slash paths stay out
 * of the substring filter/rank so fragments like {@code cat} don't match every row under parents named {@code cat11}.
 */
function categoryComposerQueryUsesInternalIdPath(q: string): boolean {
  return q.trim().includes(':');
}

function composerCategoryRank(
  row: FlatCategoryRow,
  ql: string,
  globalDup: Map<string, number>,
  includeIdPath: boolean,
): number {
  const q = ql.trim().toLowerCase();
  if (!q) return 0;
  if (!includeIdPath) {
    const s = autocompleteMatchRank(row.name, q);
    return s >= 0 ? s + 140_000 : -1;
  }
  const displayLabel = categoryPickDisplayLabel(row, globalDup);
  const scores: number[] = [
    autocompleteMatchRank(row.name, q) + 130_000,
    autocompleteMatchRank(displayLabel, q) + 110_000,
    autocompleteMatchRank(row.parentName ?? '', q) + 40_000,
    autocompleteMatchRank(row.id, q) + 75_000,
    autocompleteMatchRank(row.pathLabel, q) + 15_000,
  ];
  const ok = scores.filter((s) => s >= 0);
  return ok.length ? Math.max(...ok) : -1;
}

function composerDocumentNameRank(label: string, ql: string): number {
  return autocompleteMatchRank(label.trim(), ql);
}

function stableSortAutocompleteItems<T>(
  items: readonly T[],
  stableOrderKey: readonly T[],
  rankFn: (t: T) => number,
): T[] {
  const orderIdx = new Map<T, number>();
  stableOrderKey.forEach((t, i) => orderIdx.set(t, i));
  return [...items]
    .map((item) => ({ item, st: orderIdx.get(item) ?? 0, r: rankFn(item) }))
    .sort((a, b) => {
      if (b.r !== a.r) return b.r - a.r;
      return a.st - b.st;
    })
    .map(({ item }) => item);
}

type ComposerSuggestPick = { replaceStart: number; replaceEnd: number; label: string; insert: string };

type ComposerSuggestState = {
  picks: ComposerSuggestPick[];
  active: number;
};

function buildCategoryIdComposerPicks(
  before: string,
  query: string,
  flatCats: readonly FlatCategoryRow[],
): ComposerSuggestState | null {
  if (flatCats.length === 0) return null;
  const replaceEnd = before.length;
  const replaceStart = replaceEnd - query.length;
  const ql = query.toLowerCase();
  const q = ql.trim();
  const globalDup = countNormalizedNamesAcrossTree(flatCats);
  const idPathMatch = categoryComposerQueryUsesInternalIdPath(q);
  const filteredRows = flatCats.filter((c) => {
    if (!q) return true;
    if (idPathMatch) {
      return (
        c.id.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q) ||
        (c.parentName && c.parentName.toLowerCase().includes(q)) ||
        c.pathLabel.toLowerCase().includes(q) ||
        categoryPickDisplayLabel(c, globalDup).toLowerCase().includes(q)
      );
    }
    return c.name.toLowerCase().includes(q);
  });
  const rows = stableSortAutocompleteItems(filteredRows, flatCats, (row) =>
    composerCategoryRank(row, ql, globalDup, idPathMatch),
  ).slice(0, MAX_CHAT_SUGGESTIONS);
  const rawLabels = rows.map((r) => categoryPickDisplayLabel(r, globalDup));
  const labels = disambiguateCollidingPickLabels(rows, rawLabels);
  const picks: ComposerSuggestPick[] = rows.map((c, idx) => ({
    replaceStart,
    replaceEnd,
    label: labels[idx],
    insert: c.id,
  }));
  return picks.length ? { picks, active: 0 } : null;
}

function buildComposerSuggestState(
  fullValue: string,
  cursor: number,
  docNames: readonly string[],
  flatCats: readonly FlatCategoryRow[],
  subcaseNames: readonly string[],
  loading: boolean,
): ComposerSuggestState | null {
  if (loading) return null;
  const before = fullValue.slice(0, Math.min(Math.max(cursor, 0), fullValue.length));

  let m = before.match(DOCUMENT_NAME_TRIGGER);
  if (m && docNames.length > 0) {
    const query = m[2] ?? '';
    const replaceEnd = before.length;
    const replaceStart = replaceEnd - query.length;
    const ql = query.toLowerCase();
    const q = ql.trim();
    const filtered = docNames.filter((n) => (q ? n.toLowerCase().includes(q) : true));
    const names = stableSortAutocompleteItems(filtered, docNames, (n) => composerDocumentNameRank(n, ql)).slice(
      0,
      MAX_CHAT_SUGGESTIONS,
    );
    const picks: ComposerSuggestPick[] = names.map((name) => ({
      replaceStart,
      replaceEnd,
      label: name,
      insert: name,
    }));
    return picks.length ? { picks, active: 0 } : null;
  }

  m = before.match(RENAME_CATEGORY_TRIGGER);
  if (m) {
    const renamePicks = buildCategoryIdComposerPicks(before, m[1] ?? '', flatCats);
    if (renamePicks) return renamePicks;
  }

  m = before.match(CATEGORY_TRIGGER);
  if (m) {
    const catPicks = buildCategoryIdComposerPicks(before, m[2] ?? '', flatCats);
    if (catPicks) return catPicks;
  }

  m = before.match(SUBCASE_TRIGGER);
  if (m && subcaseNames.length > 0) {
    const query = m[1] ?? '';
    const replaceEnd = before.length;
    const replaceStart = replaceEnd - query.length;
    const ql = query.toLowerCase();
    const q = ql.trim();
    const filtered = subcaseNames.filter((n) => (q ? n.toLowerCase().includes(q) : true));
    const names = stableSortAutocompleteItems(filtered, subcaseNames, (n) => composerDocumentNameRank(n, ql)).slice(
      0,
      MAX_CHAT_SUGGESTIONS,
    );
    const picks: ComposerSuggestPick[] = names.map((name) => ({
      replaceStart,
      replaceEnd,
      label: name,
      insert: name,
    }));
    return picks.length ? { picks, active: 0 } : null;
  }

  return null;
}

/** Minimal Web Speech API typings (SpeechRecognition / webkitSpeechRecognition). */
interface WebSpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((this: WebSpeechRecognition, ev: WebSpeechRecognitionEvent) => void) | null;
  onerror: ((this: WebSpeechRecognition, ev: WebSpeechRecognitionErrorEvent) => void) | null;
  onend: ((this: WebSpeechRecognition, ev: Event) => void) | null;
}

interface WebSpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: WebSpeechRecognitionResultList;
}

interface WebSpeechRecognitionErrorEvent extends Event {
  error: string;
}

interface WebSpeechRecognitionResultList {
  length: number;
  item(index: number): WebSpeechRecognitionResult;
  [index: number]: WebSpeechRecognitionResult;
}

interface WebSpeechRecognitionResult {
  isFinal: boolean;
  length: number;
  item(index: number): WebSpeechRecognitionAlternative;
  [index: number]: WebSpeechRecognitionAlternative;
}

interface WebSpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

function getWebSpeechRecognitionCtor(): { new (): WebSpeechRecognition } | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: { new (): WebSpeechRecognition };
    webkitSpeechRecognition?: { new (): WebSpeechRecognition };
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** Persisted only for structure builder voice; independent of content editor `defaultSpeechLanguage`. */
const TREE_ASSISTANT_SPEECH_LANGUAGE_KEY = 'treeAssistantSpeechLanguage';

const TREE_ASSISTANT_SPEECH_LANG_OPTIONS: { code: string; name: string; short: string }[] = [
  { code: 'en-US', name: 'English (US)', short: 'EN' },
  { code: 'he-IL', name: 'Hebrew', short: 'HE' },
  { code: 'es-ES', name: 'Spanish', short: 'ES' },
  { code: 'fr-FR', name: 'French', short: 'FR' },
  { code: 'de-DE', name: 'German', short: 'DE' },
  { code: 'it-IT', name: 'Italian', short: 'IT' },
  { code: 'pt-BR', name: 'Portuguese', short: 'PT' },
  { code: 'ru-RU', name: 'Russian', short: 'RU' },
  { code: 'ja-JP', name: 'Japanese', short: 'JA' },
  { code: 'ko-KR', name: 'Korean', short: 'KO' },
  { code: 'zh-CN', name: 'Chinese', short: 'ZH' },
];

function treeAssistantSpeechLangOption(code: string) {
  return (
    TREE_ASSISTANT_SPEECH_LANG_OPTIONS.find((o) => o.code === code) ?? TREE_ASSISTANT_SPEECH_LANG_OPTIONS[0]
  );
}

/** Matched min height for composer text field outline and voice control card (Ask AI uses the same). */
const AI_COMPOSER_MIN_HEIGHT = 88;

const aiComposerTextFieldSx = {
  flex: 1,
  '& .MuiOutlinedInput-root': {
    minHeight: AI_COMPOSER_MIN_HEIGHT,
    height: '100%',
    alignItems: 'flex-start',
    boxSizing: 'border-box',
  },
} as const;

const aiComposerVoicePaperSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'center',
  gap: 0.75,
  flexShrink: 0,
  alignSelf: 'stretch',
  minHeight: AI_COMPOSER_MIN_HEIGHT,
  height: '100%',
  px: 0.75,
  py: 0.5,
  borderRadius: 1.5,
  bgcolor: 'background.default',
  boxShadow: 1,
  boxSizing: 'border-box',
} as const;

const aiComposerLangSelectSx = {
  fontSize: '0.8125rem',
  fontWeight: 600,
  letterSpacing: '0.04em',
  bgcolor: 'background.paper',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
  '& .MuiSelect-select': {
    py: 1,
    px: 1.25,
    minHeight: 'unset',
    lineHeight: 1.3,
    textAlign: 'center',
  },
} as const;

function normalizeTreeAssistantSpeechCode(raw: string): string {
  const c = raw.trim();
  if (!c) return 'en-US';
  if (TREE_ASSISTANT_SPEECH_LANG_OPTIONS.some((o) => o.code === c)) return c;
  const base = c.split('-')[0]?.toLowerCase() ?? '';
  const match = TREE_ASSISTANT_SPEECH_LANG_OPTIONS.find((o) => o.code.toLowerCase().startsWith(`${base}-`));
  return match?.code ?? 'en-US';
}

function readInitialTreeAssistantVoiceLang(): string {
  if (typeof localStorage === 'undefined') {
    return typeof navigator !== 'undefined' ? normalizeTreeAssistantSpeechCode(navigator.language || 'en-US') : 'en-US';
  }
  const saved = localStorage.getItem(TREE_ASSISTANT_SPEECH_LANGUAGE_KEY);
  if (saved && saved.trim()) return normalizeTreeAssistantSpeechCode(saved);
  return typeof navigator !== 'undefined'
    ? normalizeTreeAssistantSpeechCode(navigator.language || 'en-US')
    : 'en-US';
}

const TREE_ASSISTANT_PANEL_WIDTH_KEY = 'treeAssistantPanelWidth';
const TREE_ASSISTANT_PANEL_MIN_W = 300;
const TREE_ASSISTANT_PANEL_MAX_W = 800;
const TREE_ASSISTANT_PANEL_DEFAULT_W = 420;

function readTreeAssistantPanelWidth(): number {
  if (typeof localStorage === 'undefined') return TREE_ASSISTANT_PANEL_DEFAULT_W;
  const raw = localStorage.getItem(TREE_ASSISTANT_PANEL_WIDTH_KEY);
  const n = raw ? parseInt(raw, 10) : NaN;
  if (!Number.isFinite(n)) return TREE_ASSISTANT_PANEL_DEFAULT_W;
  return Math.min(TREE_ASSISTANT_PANEL_MAX_W, Math.max(TREE_ASSISTANT_PANEL_MIN_W, n));
}

function formatTreeAssistantRequestError(e: unknown): string {
  const err = e as {
    response?: { status?: number; data?: { message?: string; assistantMessage?: string } };
    message?: string;
  };
  const data = err?.response?.data;
  const serverMsg = data?.message;
  if (typeof serverMsg === 'string' && serverMsg.trim()) {
    return serverMsg.trim();
  }
  const assistantMsg = data?.assistantMessage;
  if (typeof assistantMsg === 'string' && assistantMsg.trim()) {
    return assistantMsg.trim();
  }
  if (err?.response?.status === 401) {
    return 'Session expired or not signed in. Please log in again.';
  }
  if (typeof err?.message === 'string' && err.message.trim() && err.message !== 'Network Error') {
    return err.message.trim();
  }
  return 'Request failed. Check that you are signed in and the backend is running.';
}

type ProposalViewMode = 'summary' | 'json';

/** Main panel area: AI chat vs unified paste import (outline + content leaves, or content-only). */
type TreeAssistantPanelMode = 'assistant' | 'import';

type PanelFeedbackSeverity = 'success' | 'warning' | 'error';

type PanelFeedback = {
  message: string;
  severity: PanelFeedbackSeverity;
};

function panelFeedback(message: string, severity: PanelFeedbackSeverity): PanelFeedback {
  return { message, severity };
}

function normalizeStoredPanelFeedback(raw: unknown): PanelFeedback | null {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'string') return panelFeedback(raw, 'success');
  if (typeof raw === 'object' && raw !== null && 'message' in raw) {
    const o = raw as { message?: unknown; severity?: unknown };
    const msg = typeof o.message === 'string' ? o.message : '';
    if (!msg.trim()) return null;
    const sev =
      o.severity === 'error' || o.severity === 'warning' || o.severity === 'success'
        ? o.severity
        : 'success';
    return panelFeedback(msg, sev);
  }
  return null;
}

function parsePanelMode(raw: unknown): TreeAssistantPanelMode {
  if (raw === 'import' || raw === 'assistant') {
    return raw;
  }
  if (raw === 'outline' || raw === 'bulkRows') {
    return 'import';
  }
  return 'assistant';
}

function asOpObject(op: unknown): Record<string, unknown> | null {
  if (op !== null && typeof op === 'object' && !Array.isArray(op)) {
    return op as Record<string, unknown>;
  }
  return null;
}

function strField(rec: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = rec[k];
    if (v !== null && v !== undefined && String(v).trim() !== '') {
      return String(v).trim();
    }
  }
  return '';
}

function proposalOpType(op: unknown): string {
  const o = asOpObject(op);
  if (!o) return '';
  return normalizeProposalOpType(String(o.type ?? '').trim());
}

function findDuplicateDocumentOpIndex(ops: unknown[] | null): number {
  if (!ops || !Array.isArray(ops)) return -1;
  return ops.findIndex((op) => proposalOpType(op) === 'DUPLICATE_DOCUMENT');
}

/** True when proposal starts with CREATE_DOCUMENT or DUPLICATE_DOCUMENT and only allowed follow-ups. */
function isNewDocumentCreationProposal(ops: unknown[] | null): boolean {
  if (!ops?.length) return false;
  const head = proposalOpType(ops[0]);
  if (head !== 'CREATE_DOCUMENT' && head !== 'DUPLICATE_DOCUMENT') return false;
  for (let i = 1; i < ops.length; i++) {
    const opRec = asOpObject(ops[i]);
    const t = proposalOpType(ops[i]);
    if (t === 'ADD_CATEGORY') {
      if (!opRec || strField(opRec, 'parentCategoryId') !== 'NEW_ROOT') return false;
    } else if (t === 'APPLY_CATEGORY_OUTLINE') {
      if (!opRec || strField(opRec, 'parentCategoryId') !== 'NEW_ROOT') return false;
    } else if (t === 'OPEN_DOCUMENT' || t === 'CLOSE_DOCUMENT') {
      continue;
    } else {
      return false;
    }
  }
  return true;
}

function resolveLibraryDocumentCategoryId(
  sourceName: string,
  library: TreeAssistantLibraryDocument[] | undefined,
): string | null {
  if (!library?.length || !sourceName.trim()) return null;
  const want = sourceName.trim();
  for (const d of library) {
    const nm = (d.name || '').trim();
    const id = (d.id || '').trim();
    if (nm && nm === want) return d.categoryId ?? null;
    if (id && id === want) return d.categoryId ?? null;
    if (nm && nm.toLowerCase() === want.toLowerCase()) return d.categoryId ?? null;
    if (id && id.toLowerCase() === want.toLowerCase()) return d.categoryId ?? null;
  }
  return null;
}

export type TreeAssistantLibraryDocument = {
  id: string;
  name: string;
  categoryId?: string;
};

type DuplicateSubcasePickerDialogProps = {
  open: boolean;
  sourceDocumentName: string;
  newDocumentName: string;
  categoryId: string;
  /** Tokens from the model proposal (group names and/or subcase labels) to pre-select when data loads. */
  initialSubcases: string[];
  onCancel: () => void;
  /** Empty array = copy entire document (omit subcases filter). */
  onConfirm: (subcases: string[]) => void;
};

function DuplicateSubcasePickerDialog({
  open,
  sourceDocumentName,
  newDocumentName,
  categoryId,
  initialSubcases,
  onCancel,
  onConfirm,
}: DuplicateSubcasePickerDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allSubcases, setAllSubcases] = useState<string[]>([]);
  const [groups, setGroups] = useState<Record<string, string[]>>({});
  const [mode, setMode] = useState<'all' | 'pick'>('all');
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());
  const [selectedSubcases, setSelectedSubcases] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open || !categoryId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setAllSubcases([]);
    setGroups({});
    setMode(initialSubcases.length > 0 ? 'pick' : 'all');
    setSelectedGroups(new Set());
    setSelectedSubcases(new Set());

    (async () => {
      try {
        const [subRes, grpRes] = await Promise.all([
          api.get<string[]>('/documents/subcases', { params: { documentId: categoryId } }),
          api.get<Record<string, string[]>>('/documents/subcase-groups', { params: { documentId: categoryId } }),
        ]);
        if (cancelled) return;
        const subs = Array.isArray(subRes.data) ? subRes.data.map((s) => String(s).trim()).filter(Boolean) : [];
        const grpRaw = grpRes.data && typeof grpRes.data === 'object' && !Array.isArray(grpRes.data) ? grpRes.data : {};
        const grp: Record<string, string[]> = {};
        for (const [k, v] of Object.entries(grpRaw)) {
          const key = String(k).trim();
          if (!key) continue;
          grp[key] = Array.isArray(v) ? v.map((x) => String(x).trim()).filter(Boolean) : [];
        }
        setAllSubcases(subs);
        setGroups(grp);

        const groupNames = new Set(Object.keys(grp));
        const nextG = new Set<string>();
        const nextS = new Set<string>();
        for (const t of initialSubcases.map((x) => String(x).trim()).filter(Boolean)) {
          if (groupNames.has(t)) nextG.add(t);
          else nextS.add(t);
        }
        setSelectedGroups(nextG);
        setSelectedSubcases(nextS);
      } catch (e) {
        if (!cancelled) {
          setError(formatTreeAssistantRequestError(e));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, categoryId, initialSubcases]);

  const toggleGroup = (name: string) => {
    setSelectedGroups((prev) => {
      const n = new Set(prev);
      if (n.has(name)) n.delete(name);
      else n.add(name);
      return n;
    });
  };

  const toggleSubcase = (name: string) => {
    setSelectedSubcases((prev) => {
      const n = new Set(prev);
      if (n.has(name)) n.delete(name);
      else n.add(name);
      return n;
    });
  };

  const handleConfirm = () => {
    if (mode === 'all') {
      onConfirm([]);
      return;
    }
    const out: string[] = [];
    const sortedGroups = Array.from(selectedGroups).sort((a, b) => a.localeCompare(b));
    const sortedSubs = Array.from(selectedSubcases).sort((a, b) => a.localeCompare(b));
    out.push(...sortedGroups, ...sortedSubs);
    if (out.length === 0) {
      setError('Choose at least one subcase group or subcase, or switch to "Full document".');
      return;
    }
    onConfirm(out);
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        onCancel();
      }}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>Duplicate: subcases and groups</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Source <strong>{sourceDocumentName || '—'}</strong> → new document <strong>{newDocumentName || '—'}</strong>
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
            <CircularProgress size={22} />
            <Typography variant="body2">Loading subcases…</Typography>
          </Box>
        ) : (
          <>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              What to copy
            </Typography>
            <RadioGroup
              value={mode}
              onChange={(_, v) => setMode(v as 'all' | 'pick')}
              sx={{ mb: 1 }}
            >
              <FormControlLabel value="all" control={<Radio size="small" />} label="Full document (all subcases)" />
              <FormControlLabel
                value="pick"
                control={<Radio size="small" />}
                label="Only selected groups and/or subcases"
              />
            </RadioGroup>
            {mode === 'pick' && (
              <>
                {Object.keys(groups).length > 0 && (
                  <>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, mb: 0.5 }}>
                      Subcase groups (server expands each group to its members)
                    </Typography>
                    <Stack spacing={0.25} sx={{ mb: 1.5 }}>
                      {Object.keys(groups)
                        .sort((a, b) => a.localeCompare(b))
                        .map((g) => (
                          <FormControlLabel
                            key={g}
                            control={
                              <Checkbox
                                size="small"
                                checked={selectedGroups.has(g)}
                                onChange={() => toggleGroup(g)}
                              />
                            }
                            label={g}
                          />
                        ))}
                    </Stack>
                  </>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Individual subcases (row labels). You can combine with groups; the server expands group names.
                  </Typography>
                  <InvertSelectionButton
                    disabled={allSubcases.length === 0}
                    onClick={() => {
                      setSelectedGroups(new Set());
                      setSelectedSubcases(
                        new Set(invertSubcaseSelection(allSubcases, Array.from(selectedSubcases))),
                      );
                    }}
                  />
                </Box>
                <Stack spacing={0.25} sx={{ maxHeight: 220, overflow: 'auto' }}>
                  {allSubcases.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No subcases returned for this document.
                    </Typography>
                  ) : (
                    allSubcases.map((s) => (
                      <FormControlLabel
                        key={s}
                        control={
                          <Checkbox
                            size="small"
                            checked={selectedSubcases.has(s)}
                            onChange={() => toggleSubcase(s)}
                          />
                        }
                        label={s}
                      />
                    ))
                  )}
                </Stack>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={loading}>
          Continue to apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function normalizeProposalOpType(raw: string): string {
  const u = raw.replace(/\s+/g, '_').toUpperCase();
  if (
    u === 'SELECT_DOCUMENT' ||
    u === 'SELECT_DOC' ||
    u === 'ACTIVATE_DOCUMENT' ||
    u === 'SWITCH_TO_DOCUMENT' ||
    u === 'FOCUS_DOCUMENT'
  ) {
    return 'OPEN_DOCUMENT';
  }
  return u;
}

function parseTreeAssistantClientActions(raw: unknown): TreeAssistantClientAction[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  const out: TreeAssistantClientAction[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    const t =
      typeof o.type === 'string' ? o.type.replace(/\s+/g, '_').toUpperCase() : '';
    if (t === 'OPEN_DOCUMENT') {
      const documentName = typeof o.documentName === 'string' ? o.documentName.trim() : '';
      const categoryId = typeof o.categoryId === 'string' ? o.categoryId.trim() : '';
      if (documentName && categoryId) {
        out.push({ type: 'OPEN_DOCUMENT', documentName, categoryId });
      }
    } else if (t === 'CLOSE_DOCUMENT') {
      const documentName = typeof o.documentName === 'string' ? o.documentName.trim() : '';
      if (documentName) {
        out.push({ type: 'CLOSE_DOCUMENT', documentName });
      }
    }
  }
  return out.length > 0 ? out : undefined;
}

/**
 * Rejected proposals have no {@code proposalId}; do not show operations / organize preview (not applicable).
 */
function proposalUiFromTurnResponse(data: {
  proposalId?: unknown;
  operations?: unknown;
  organizePreview?: unknown;
}): { proposalId: string | null; proposalOps: unknown[] | null; organizePreview: unknown } {
  const rawPid = data.proposalId;
  const proposalId =
    rawPid !== undefined && rawPid !== null && String(rawPid).trim() !== ''
      ? String(rawPid).trim()
      : null;
  if (!proposalId) {
    return { proposalId: null, proposalOps: null, organizePreview: null };
  }
  const rawOps = data.operations;
  const proposalOps =
    rawOps !== undefined && rawOps !== null && Array.isArray(rawOps) ? rawOps : null;
  return { proposalId, proposalOps, organizePreview: data.organizePreview ?? null };
}

/** Server-built layout for ORGANIZE_CONTENT_CATEGORY_ONE_LAYER (matches apply when rows unchanged). */
function OrganizePreviewPanel({ preview }: { preview: unknown }) {
  if (preview === null || preview === undefined || typeof preview !== 'object') {
    return null;
  }
  const buckets = (preview as { buckets?: unknown }).buckets;
  if (!Array.isArray(buckets) || buckets.length === 0) {
    return null;
  }
  return (
    <Stack spacing={1.5} sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        Preview: how rows will be grouped into folders (apply only if rows are unchanged).
      </Typography>
      {buckets.map((raw, i) => {
        const b = raw as { folderName?: string; rows?: unknown[] };
        const name = typeof b.folderName === 'string' && b.folderName.trim() ? b.folderName : 'Folder';
        const rows = Array.isArray(b.rows) ? b.rows : [];
        return (
          <Paper key={i} variant="outlined" sx={{ p: 1.25 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
              {name}
            </Typography>
            {rows.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                (no rows)
              </Typography>
            ) : (
              <Stack component="ul" sx={{ m: 0, pl: 2, gap: 0.5 }}>
                {rows.map((rr, j) => {
                  const r = rr as { subcase?: string; textPreview?: string };
                  const sub = typeof r.subcase === 'string' ? r.subcase.trim() : '';
                  const txt = typeof r.textPreview === 'string' ? r.textPreview : '';
                  return (
                    <Typography
                      key={j}
                      component="li"
                      variant="body2"
                      color="text.secondary"
                      sx={{ display: 'list-item', whiteSpace: 'pre-wrap' }}
                    >
                      {sub ? (
                        <>
                          <Box component="span" sx={{ fontWeight: 600 }}>
                            {sub}
                          </Box>
                          :{' '}
                        </>
                      ) : null}
                      {txt || '—'}
                    </Typography>
                  );
                })}
              </Stack>
            )}
          </Paper>
        );
      })}
    </Stack>
  );
}

function renderOutlinePreviewBranch(items: unknown[]): React.ReactNode {
  return items.map((raw, i) => {
    const rec = asOpObject(raw);
    const name = rec ? strField(rec, 'name') : '';
    const children =
      rec && Array.isArray((rec as Record<string, unknown>).children)
        ? ((rec as Record<string, unknown>).children as unknown[])
        : [];
    return (
      <Box component="li" key={i} sx={{ display: 'list-item', my: 0.25 }}>
        <Typography variant="body2" component="span">
          {name || '—'}
        </Typography>
        {children.length > 0 ? (
          <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.25 }}>
            {renderOutlinePreviewBranch(children)}
          </Box>
        ) : null}
      </Box>
    );
  });
}

/** First APPLY_CATEGORY_OUTLINE `nodes` in the proposal, for a compact tree preview. */
function OutlinePreviewPanel({ ops }: { ops: unknown[] | null }) {
  let nodes: unknown[] | null = null;
  if (ops) {
    for (const raw of ops) {
      const rec = asOpObject(raw);
      if (!rec) continue;
      if (normalizeProposalOpType(strField(rec, 'type')) !== 'APPLY_CATEGORY_OUTLINE') continue;
      const rawNodes = (rec as Record<string, unknown>).nodes ?? (rec as Record<string, unknown>).outline;
      if (Array.isArray(rawNodes) && rawNodes.length > 0) {
        nodes = rawNodes;
        break;
      }
    }
  }
  if (!nodes || nodes.length === 0) return null;
  return (
    <Stack spacing={1} sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        Outline preview (folder names)
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2, py: 0 }}>
        {renderOutlinePreviewBranch(nodes)}
      </Box>
    </Stack>
  );
}

function isNewRootCategoryId(catId: string): boolean {
  const u = catId.trim().toUpperCase();
  return u === 'NEW_ROOT' || u === 'NEW ROOT';
}

function shortOpaqueId(id: string): string {
  const t = id.trim();
  if (t.length <= 14) return t;
  return `${t.slice(0, 10)}…`;
}

/** Plain-language category label for proposals (path when known; no raw ids in the main text). */
function categorySummaryLabel(
  catId: string,
  flatCats: readonly FlatCategoryRow[] | undefined,
): string {
  const id = catId.trim();
  if (!id) return '—';
  if (isNewRootCategoryId(id)) return 'document root (new document)';
  if (!flatCats?.length) return `category ${shortOpaqueId(id)}`;
  const row = flatCats.find((c) => c.id === id);
  if (!row) return `category ${shortOpaqueId(id)}`;
  return row.pathLabel;
}

function formatProposalCategoryRef(
  catId: string,
  flatCats: readonly FlatCategoryRow[] | undefined,
): React.ReactNode {
  const label = categorySummaryLabel(catId, flatCats);
  if (!catId.trim()) return '—';
  return <strong>{label}</strong>;
}

function proposalOpOneLine(
  rec: Record<string, unknown> | null,
  type: string,
  flatCats: readonly FlatCategoryRow[] | undefined,
): string {
  if (!rec || !type) return 'Unknown step';
  const q = (s: string) => (s ? `"${s}"` : '—');
  switch (type) {
    case 'CREATE_DOCUMENT': {
      const name = strField(rec, 'documentName', 'name');
      const rawCats = rec.categories;
      let n = 0;
      if (Array.isArray(rawCats)) n = rawCats.length;
      const rawNodes = rec.nodes ?? rec.outline;
      if (n === 0 && Array.isArray(rawNodes)) {
        const walk = (arr: unknown) => {
          if (!Array.isArray(arr)) return;
          for (const item of arr) {
            if (item && typeof item === 'object' && !Array.isArray(item)) n++;
            const ch = (item as Record<string, unknown>).children;
            walk(ch);
          }
        };
        walk(rawNodes);
      }
      return n > 0
        ? `Create document ${q(name)} with ${n} top-level ${n === 1 ? 'category' : 'categories'}`
        : `Create empty document ${q(name)}`;
    }
    case 'DELETE_DOCUMENT':
      return `Delete document ${q(strField(rec, 'documentName', 'documentId', 'name'))} and everything in it`;
    case 'DUPLICATE_DOCUMENT':
      return `Duplicate ${q(strField(rec, 'sourceDocumentName', 'documentName'))} as ${q(
        strField(rec, 'newDocumentName', 'name'),
      )}`;
    case 'RENAME_DOCUMENT':
      return `Rename document ${q(strField(rec, 'currentDocumentName', 'documentName'))} to ${q(
        strField(rec, 'newDocumentName', 'newName'),
      )}`;
    case 'OPEN_DOCUMENT':
      return `Open document ${q(strField(rec, 'documentName', 'documentId', 'name'))}`;
    case 'CLOSE_DOCUMENT':
      return `Close tab for ${q(strField(rec, 'documentName', 'documentId', 'name'))}`;
    case 'UPDATE_CATEGORY_DESCRIPTION':
      return `Update description for ${categorySummaryLabel(strField(rec, 'categoryId', 'id'), flatCats)}`;
    case 'CREATE_CONTENT': {
      const sub = strField(rec, 'subcase', 'contentSubcase');
      const parent = strField(rec, 'parentCategoryId', 'parentId');
      return `Add content row ${q(sub)} under ${categorySummaryLabel(parent, flatCats)}`;
    }
    case 'BULK_CREATE_CONTENT': {
      const parent = strField(rec, 'parentCategoryId', 'parentId', 'contentCategoryId');
      const rawLines = (rec as Record<string, unknown>).lines;
      const rowCount = Array.isArray(rawLines) ? rawLines.length : 0;
      return `Add ${rowCount || '—'} content row(s) under ${categorySummaryLabel(parent, flatCats)}`;
    }
    case 'MOVE_CONTENT':
      return `Move a content row into ${categorySummaryLabel(
        strField(rec, 'newParentCategoryId', 'newParentId', 'parentCategoryId'),
        flatCats,
      )}`;
    case 'COPY_CONTENT':
      return `Copy a content row into ${categorySummaryLabel(
        strField(rec, 'newParentCategoryId', 'newParentId', 'parentCategoryId'),
        flatCats,
      )}`;
    case 'DELETE_CONTENT':
      return 'Delete one content row';
    case 'DELETE_ALL_CONTENT_IN_CATEGORY':
      return `Clear all content rows in ${categorySummaryLabel(
        strField(rec, 'contentCategoryId', 'categoryId'),
        flatCats,
      )}`;
    case 'APPLY_CATEGORY_OUTLINE': {
      const pid = strField(rec, 'parentCategoryId', 'anchorCategoryId', 'parentId');
      const rawNodes = (rec as Record<string, unknown>).nodes ?? (rec as Record<string, unknown>).outline;
      let nodeCount = 0;
      let rowCount = 0;
      const walk = (arr: unknown) => {
        if (!Array.isArray(arr)) return;
        for (const item of arr) {
          if (!item || typeof item !== 'object') continue;
          nodeCount++;
          const lines = (item as Record<string, unknown>).lines;
          if (Array.isArray(lines)) {
            rowCount += lines.length;
          }
          walk((item as Record<string, unknown>).children);
        }
      };
      walk(rawNodes);
      const under = isNewRootCategoryId(pid)
        ? 'document root (new document)'
        : categorySummaryLabel(pid, flatCats);
      const folderPart = `Add ${nodeCount || '—'} nested ${nodeCount === 1 ? 'folder' : 'folders'} under ${under}`;
      return rowCount > 0 ? `${folderPart} with ${rowCount} content row(s)` : folderPart;
    }
    case 'ORGANIZE_CONTENT_CATEGORY_ONE_LAYER':
      return `Organize content rows in ${categorySummaryLabel(
        strField(rec, 'contentCategoryId', 'categoryId'),
        flatCats,
      )} into AI-suggested subfolders`;
    case 'RENAME_SUBCASE':
      return `Rename subcase ${q(strField(rec, 'oldSubcase', 'fromSubcase'))} to ${q(
        strField(rec, 'newSubcase', 'toSubcase'),
      )} in ${categorySummaryLabel(strField(rec, 'contentCategoryId', 'categoryId'), flatCats)}`;
    case 'ADD_CATEGORY':
      return `Add category ${q(strField(rec, 'name'))} under ${categorySummaryLabel(
        strField(rec, 'parentCategoryId', 'parentId'),
        flatCats,
      )}`;
    case 'SPLIT_CATEGORY': {
      const contentId = strField(rec, 'contentCategoryId');
      const rawNames = rec.childNames;
      const childNames = Array.isArray(rawNames)
        ? (rawNames as unknown[]).map((x) => String(x).trim()).filter(Boolean)
        : [];
      const first = strField(rec, 'firstName');
      const second = strField(rec, 'secondName');
      const namesLabel =
        childNames.length >= 2 ? childNames.join(', ') : [first, second].filter(Boolean).join(' and ') || '—';
      return `Split ${categorySummaryLabel(contentId, flatCats)} into: ${namesLabel}`;
    }
    case 'RENAME_CATEGORY':
      return `Rename ${categorySummaryLabel(strField(rec, 'categoryId', 'id'), flatCats)} to ${q(
        strField(rec, 'newName', 'name'),
      )}`;
    case 'MOVE_CATEGORY':
      return `Move ${categorySummaryLabel(strField(rec, 'categoryId', 'id'), flatCats)} under ${categorySummaryLabel(
        strField(rec, 'newParentCategoryId', 'newParentId', 'parentCategoryId'),
        flatCats,
      )}`;
    case 'COPY_CATEGORY': {
      const newName = strField(rec, 'newName', 'name');
      const base = `Copy ${categorySummaryLabel(strField(rec, 'categoryId', 'id'), flatCats)} under ${categorySummaryLabel(
        strField(rec, 'newParentCategoryId', 'newParentId', 'parentCategoryId'),
        flatCats,
      )}`;
      return newName ? `${base} as ${q(newName)}` : base;
    }
    case 'DELETE_CATEGORY':
      return `Delete ${categorySummaryLabel(strField(rec, 'categoryId', 'id'), flatCats)} and everything under it`;
    default:
      return type.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
  }
}

function buildProposalOverviewText(
  ops: unknown[],
  flatCats: readonly FlatCategoryRow[] | undefined,
): string {
  if (ops.length === 0) return 'No changes in this plan.';
  const head =
    ops.length === 1 ? '1 change ready to apply:' : `${ops.length} changes ready to apply:`;
  const lines = ops.map((raw, i) => {
    const rec = asOpObject(raw);
    const type = rec ? normalizeProposalOpType(strField(rec, 'type')) : '';
    return `${i + 1}. ${proposalOpOneLine(rec, type, flatCats)}`;
  });
  return `${head}\n${lines.join('\n')}`;
}

function formatApplySuccessChatMessage(
  ops: unknown[] | null,
  flatCats: readonly FlatCategoryRow[] | undefined,
  data: {
    message?: unknown;
    newDocumentName?: unknown;
    deletedDocumentName?: unknown;
    renamedDocumentOldName?: unknown;
    renamedDocumentNewName?: unknown;
  },
): string {
  const parts: string[] = [];
  if (ops && ops.length > 0) {
    parts.push(
      ops.length === 1 ? 'Done — 1 change applied:' : `Done — ${ops.length} changes applied:`,
    );
    for (const raw of ops) {
      const rec = asOpObject(raw);
      const type = rec ? normalizeProposalOpType(strField(rec, 'type')) : '';
      parts.push(`• ${proposalOpOneLine(rec, type, flatCats)}`);
    }
  } else {
    const fallback = String(data.message ?? '').trim();
    parts.push(fallback || 'Changes applied successfully.');
  }
  const extras: string[] = [];
  const newDoc = data.newDocumentName;
  if (newDoc !== undefined && newDoc !== null && String(newDoc).trim()) {
    extras.push(`Opened new document: ${String(newDoc).trim()}`);
  }
  const deleted = data.deletedDocumentName;
  if (deleted !== undefined && deleted !== null && String(deleted).trim()) {
    extras.push(`Removed document: ${String(deleted).trim()}`);
  }
  const rOld = data.renamedDocumentOldName;
  const rNew = data.renamedDocumentNewName;
  if (rOld && String(rOld).trim() && rNew && String(rNew).trim()) {
    extras.push(`Renamed document: ${String(rOld).trim()} → ${String(rNew).trim()}`);
  }
  if (extras.length > 0) {
    parts.push('', ...extras);
  }
  return parts.join('\n');
}

function ProposalSummary({
  ops,
  flatCats,
}: {
  ops: unknown[];
  flatCats?: readonly FlatCategoryRow[];
}) {
  if (ops.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No operations in this plan.
      </Typography>
    );
  }
  return (
    <Stack spacing={1.25} sx={{ width: '100%' }}>
      {ops.map((raw, i) => {
        const rec = asOpObject(raw);
        const type = rec ? normalizeProposalOpType(strField(rec, 'type')) : '';
        const step = i + 1;
        if (!rec || !type) {
          return (
            <Paper key={i} variant="outlined" sx={{ p: 1.5, borderStyle: 'dashed' }}>
              <Typography variant="caption" color="text.secondary">
                Step {step}
              </Typography>
              <Typography variant="body2">Unknown or empty operation (see JSON view).</Typography>
            </Paper>
          );
        }
        if (type === 'CREATE_DOCUMENT') {
          const name = strField(rec, 'documentName', 'name');
          const rawCats = rec.categories;
          const catNames: string[] = Array.isArray(rawCats)
            ? rawCats
                .filter((x): x is string => typeof x === 'string' && x.trim().length > 0)
                .map((x) => x.trim())
            : [];
          const rawNodes = rec.nodes ?? rec.outline;
          if (catNames.length === 0 && Array.isArray(rawNodes)) {
            for (const n of rawNodes) {
              if (n && typeof n === 'object' && !Array.isArray(n)) {
                const nm = strField(n as Record<string, unknown>, 'name');
                if (nm) catNames.push(nm);
              }
            }
          }
          return (
            <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <PostAddIcon color="primary" fontSize="small" sx={{ mt: 0.25 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Step {step} · New document
                  </Typography>
                  <Typography variant="body2">
                    Create document <strong>{name || '(unnamed)'}</strong>
                    {catNames.length > 0 ? (
                      <>
                        {' '}
                        with {catNames.length} root{' '}
                        {catNames.length === 1 ? 'category' : 'categories'}:{' '}
                        <strong>{catNames.slice(0, 8).join(', ')}</strong>
                        {catNames.length > 8 ? '…' : ''}
                      </>
                    ) : (
                      ' (empty root)'
                    )}
                    .
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        }
        if (type === 'DELETE_DOCUMENT') {
          const name = strField(rec, 'documentName', 'documentId', 'name');
          return (
            <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <DeleteOutlineIcon color="error" fontSize="small" sx={{ mt: 0.25 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Step {step} · Delete document
                  </Typography>
                  <Typography variant="body2">
                    Permanently remove the document <strong>{name || '(unnamed)'}</strong> and everything inside it.
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        }
        if (type === 'DUPLICATE_DOCUMENT') {
          const src = strField(rec, 'sourceDocumentName', 'documentName');
          const newName = strField(rec, 'newDocumentName', 'name');
          return (
            <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <FileCopyOutlinedIcon color="primary" fontSize="small" sx={{ mt: 0.25 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Step {step} · Duplicate document
                  </Typography>
                  <Typography variant="body2" component="div">
                    Copy document <strong>{src || '—'}</strong> to a new document named <strong>{newName || '—'}</strong>
                    {Array.isArray(rec.subcases) && (rec.subcases as unknown[]).length > 0
                      ? ' (only listed subcases or subcase groups — see JSON; groups expand on apply).'
                      : '.'}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        }
        if (type === 'RENAME_DOCUMENT') {
          const from = strField(rec, 'currentDocumentName', 'documentName');
          const to = strField(rec, 'newDocumentName', 'newName');
          return (
            <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <DriveFileRenameOutlineIcon color="primary" fontSize="small" sx={{ mt: 0.25 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Step {step} · Rename document
                  </Typography>
                  <Typography variant="body2" component="div">
                    Rename library document <strong>{from || '—'}</strong> to <strong>{to || '—'}</strong>. (No tree-assistant
                    undo.)
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        }
        if (type === 'OPEN_DOCUMENT') {
          const name = strField(rec, 'documentName', 'documentId', 'name');
          return (
            <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <FolderOpenIcon color="primary" fontSize="small" sx={{ mt: 0.25 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Step {step} · Select document
                  </Typography>
                  <Typography variant="body2">
                    Select <strong>{name || '(unnamed)'}</strong> — same as clicking its tab (activate if already open; open if closed).
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        }
        if (type === 'CLOSE_DOCUMENT') {
          const name = strField(rec, 'documentName', 'documentId', 'name');
          return (
            <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <TabUnselectedIcon color="action" fontSize="small" sx={{ mt: 0.25 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Step {step} · Close document tab
                  </Typography>
                  <Typography variant="body2">
                    Close the tab for <strong>{name || '(unnamed)'}</strong> if it is open (does not delete the document).
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        }
        if (type === 'UPDATE_CATEGORY_DESCRIPTION') {
          const catId = strField(rec, 'categoryId', 'id');
          const desc = strField(rec, 'description', 'newDescription');
          return (
            <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <EditNoteOutlinedIcon color="info" fontSize="small" sx={{ mt: 0.25 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Step {step} · Update category description
                  </Typography>
                  <Typography variant="body2" component="div">
                    Update description for {formatProposalCategoryRef(catId, flatCats)}.
                  </Typography>
                  {desc ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, whiteSpace: 'pre-wrap' }}>
                      {desc.length > 240 ? `${desc.slice(0, 240)}…` : desc}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            </Paper>
          );
        }
        if (type === 'CREATE_CONTENT') {
          const parent = strField(rec, 'parentCategoryId', 'parentId');
          const sub = strField(rec, 'subcase', 'contentSubcase');
          const text = strField(rec, 'text');
          return (
            <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <NoteAddOutlinedIcon color="primary" fontSize="small" sx={{ mt: 0.25 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Step {step} · Add content row
                  </Typography>
                  <Typography variant="body2" component="div">
                    Under {formatProposalCategoryRef(parent, flatCats)}, add subcase <strong>{sub || '—'}</strong>
                    {text ? '.' : ' (empty body).'}
                  </Typography>
                  {text ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, whiteSpace: 'pre-wrap' }}>
                      {text.length > 120 ? `${text.slice(0, 120)}…` : text}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            </Paper>
          );
        }
        if (type === 'BULK_CREATE_CONTENT') {
          const parent = strField(rec, 'parentCategoryId', 'parentId', 'contentCategoryId');
          const rawLines = (rec as Record<string, unknown>).lines;
          let rowCount = 0;
          if (Array.isArray(rawLines)) {
            rowCount = rawLines.length;
          }
          return (
            <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <NoteAddOutlinedIcon color="primary" fontSize="small" sx={{ mt: 0.25 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Step {step} · Bulk add content rows
                  </Typography>
                  <Typography variant="body2" component="div">
                    Add <strong>{rowCount > 0 ? String(rowCount) : '—'}</strong> row(s) under{' '}
                    {formatProposalCategoryRef(parent, flatCats)}. Undo removes rows in reverse order.
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        }
        if (type === 'MOVE_CONTENT') {
          const np = strField(rec, 'newParentCategoryId', 'newParentId', 'parentCategoryId');
          return (
            <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <DriveFileMoveOutlinedIcon color="secondary" fontSize="small" sx={{ mt: 0.25 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Step {step} · Move content
                  </Typography>
                  <Typography variant="body2" component="div">
                    Move one content row into {formatProposalCategoryRef(np, flatCats)}.
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        }
        if (type === 'COPY_CONTENT') {
          const np = strField(rec, 'newParentCategoryId', 'newParentId', 'parentCategoryId');
          return (
            <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <FileCopyOutlinedIcon color="secondary" fontSize="small" sx={{ mt: 0.25 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Step {step} · Copy content
                  </Typography>
                  <Typography variant="body2" component="div">
                    Copy one content row into {formatProposalCategoryRef(np, flatCats)}.
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        }
        if (type === 'DELETE_CONTENT') {
          const cid = strField(rec, 'contentId', 'id');
          return (
            <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <DeleteOutlineIcon color="warning" fontSize="small" sx={{ mt: 0.25 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Step {step} · Delete content
                  </Typography>
                  <Typography variant="body2" component="div">
                    Delete one content row{cid ? ` (${shortOpaqueId(cid)})` : ''}.
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        }
        if (type === 'DELETE_ALL_CONTENT_IN_CATEGORY') {
          const cc = strField(rec, 'contentCategoryId', 'categoryId');
          return (
            <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <DeleteOutlineIcon color="warning" fontSize="small" sx={{ mt: 0.25 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Step {step} · Clear category content
                  </Typography>
                  <Typography variant="body2" component="div">
                    Delete all content rows in {formatProposalCategoryRef(cc, flatCats)} (the folder stays).
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        }
        if (type === 'APPLY_CATEGORY_OUTLINE') {
          const pid = strField(rec, 'parentCategoryId', 'anchorCategoryId', 'parentId');
          const rawNodes = (rec as Record<string, unknown>).nodes ?? (rec as Record<string, unknown>).outline;
          let nodeCount = 0;
          let rowCount = 0;
          const walk = (arr: unknown) => {
            if (!Array.isArray(arr)) return;
            for (const item of arr) {
              if (!item || typeof item !== 'object') continue;
              nodeCount++;
              const lines = (item as Record<string, unknown>).lines;
              if (Array.isArray(lines)) {
                rowCount += lines.length;
              }
              walk((item as Record<string, unknown>).children);
            }
          };
          walk(rawNodes);
          return (
            <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <AccountTreeIcon color="primary" fontSize="small" sx={{ mt: 0.25 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Step {step} · Category outline
                  </Typography>
                  <Typography variant="body2" component="div">
                    Create {nodeCount > 0 ? `${nodeCount} nested ` : ''}
                    {nodeCount === 1 ? 'branch folder' : 'branch folders'} under{' '}
                    {formatProposalCategoryRef(pid, flatCats)}
                    {rowCount > 0 ? (
                      <>
                        {' '}
                        with <strong>{rowCount}</strong> content row{rowCount === 1 ? '' : 's'}
                      </>
                    ) : null}
                    .
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        }
        if (type === 'ORGANIZE_CONTENT_CATEGORY_ONE_LAYER') {
          const cc = strField(rec, 'contentCategoryId', 'categoryId');
          const ctx = strField(rec, 'context');
          return (
            <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <AutoAwesomeOutlinedIcon color="secondary" fontSize="small" sx={{ mt: 0.25 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Step {step} · Organize content (one layer)
                  </Typography>
                  <Typography variant="body2" component="div">
                    Organize rows in {formatProposalCategoryRef(cc, flatCats)} into AI-suggested subfolders (one
                    level deep).
                  </Typography>
                  {ctx ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, whiteSpace: 'pre-wrap' }}>
                      Context: {ctx.length > 200 ? `${ctx.slice(0, 200)}…` : ctx}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            </Paper>
          );
        }
        if (type === 'RENAME_SUBCASE') {
          const cc = strField(rec, 'contentCategoryId', 'categoryId');
          const oldS = strField(rec, 'oldSubcase', 'fromSubcase');
          const newS = strField(rec, 'newSubcase', 'toSubcase');
          return (
            <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <SubtitlesOutlinedIcon color="info" fontSize="small" sx={{ mt: 0.25 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Step {step} · Rename subcase
                  </Typography>
                  <Typography variant="body2" component="div">
                    In {formatProposalCategoryRef(cc, flatCats)}, rename subcase <strong>{oldS || '—'}</strong> →{' '}
                    <strong>{newS || '—'}</strong> on all matching rows.
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        }
        if (type === 'ADD_CATEGORY') {
          const catName = strField(rec, 'name');
          const parent = strField(rec, 'parentCategoryId', 'parentId');
          const desc = strField(rec, 'description');
          return (
            <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <CreateNewFolderIcon color="primary" fontSize="small" sx={{ mt: 0.25 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Step {step} · Add category
                  </Typography>
                  <Typography variant="body2" component="div">
                    Add category <strong>{catName || '(unnamed)'}</strong> under{' '}
                    {formatProposalCategoryRef(parent, flatCats)}.
                  </Typography>
                  {desc ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Description: {desc}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            </Paper>
          );
        }
        if (type === 'SPLIT_CATEGORY') {
          const contentId = strField(rec, 'contentCategoryId');
          const rawNames = rec.childNames;
          const childNames = Array.isArray(rawNames)
            ? (rawNames as unknown[]).map((x) => String(x).trim()).filter(Boolean)
            : [];
          const first = strField(rec, 'firstName');
          const second = strField(rec, 'secondName');
          const namesLabel =
            childNames.length >= 2
              ? childNames.join(', ')
              : [first, second].filter(Boolean).join(' and ') || '—';
          const count = childNames.length >= 2 ? childNames.length : first && second ? 2 : 0;
          return (
            <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <AltRouteIcon color="secondary" fontSize="small" sx={{ mt: 0.25 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Step {step} · Split content category
                  </Typography>
                  <Typography variant="body2" component="div">
                    Split {formatProposalCategoryRef(contentId, flatCats)} into {count > 0 ? count : '—'}{' '}
                    subcategories: <strong>{namesLabel}</strong>.
                    {count === 2
                      ? ' Selected content rows in the editor go to the first child when assigned (same as toolbar Split).'
                      : ' Existing rows go to a temporary bucket unless contentByChildName assigns them.'}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        }
        if (type === 'RENAME_CATEGORY') {
          const catId = strField(rec, 'categoryId', 'id');
          const newName = strField(rec, 'newName', 'name');
          return (
            <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <EditIcon color="info" fontSize="small" sx={{ mt: 0.25 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Step {step} · Rename category
                  </Typography>
                  <Typography variant="body2" component="div">
                    Rename {formatProposalCategoryRef(catId, flatCats)} to <strong>{newName || '(unnamed)'}</strong>.
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        }
        if (type === 'MOVE_CATEGORY') {
          const catId = strField(rec, 'categoryId', 'id');
          const newParent = strField(rec, 'newParentCategoryId', 'newParentId', 'parentCategoryId');
          return (
            <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <DriveFileMoveOutlinedIcon color="primary" fontSize="small" sx={{ mt: 0.25 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Step {step} · Move category
                  </Typography>
                  <Typography variant="body2" component="div">
                    Move {formatProposalCategoryRef(catId, flatCats)} under{' '}
                    {formatProposalCategoryRef(newParent, flatCats)}.
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        }
        if (type === 'COPY_CATEGORY') {
          const catId = strField(rec, 'categoryId', 'id');
          const newParent = strField(rec, 'newParentCategoryId', 'newParentId', 'parentCategoryId');
          const newName = strField(rec, 'newName', 'name');
          return (
            <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <FileCopyOutlinedIcon color="primary" fontSize="small" sx={{ mt: 0.25 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Step {step} · Copy category branch
                  </Typography>
                  <Typography variant="body2" component="div">
                    Copy {formatProposalCategoryRef(catId, flatCats)} under{' '}
                    {formatProposalCategoryRef(newParent, flatCats)}
                    {newName ? (
                      <>
                        {' '}
                        as <strong>{newName}</strong>.
                      </>
                    ) : (
                      '.'
                    )}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        }
        if (type === 'DELETE_CATEGORY') {
          const catId = strField(rec, 'categoryId', 'id');
          return (
            <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <FolderDeleteOutlinedIcon color="warning" fontSize="small" sx={{ mt: 0.25 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Step {step} · Delete category branch
                  </Typography>
                  <Typography variant="body2" component="div">
                    Delete {formatProposalCategoryRef(catId, flatCats)} and everything under it (not the whole
                    document).
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        }
        return (
          <Paper key={i} variant="outlined" sx={{ p: 1.5, borderStyle: 'dashed' }}>
            <Typography variant="caption" color="text.secondary">
              Step {step}
            </Typography>
            <Typography variant="body2">{proposalOpOneLine(rec, type, flatCats)}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Switch to JSON for full fields ({type}).
            </Typography>
          </Paper>
        );
      })}
    </Stack>
  );
}

function TreeAssistantProposalSection({
  proposalOps,
  proposalViewMode,
  onProposalViewMode,
  proposalJsonText,
  proposalId,
  organizePreview,
  flatCats,
}: {
  proposalOps: unknown[] | null;
  proposalViewMode: ProposalViewMode;
  onProposalViewMode: (mode: ProposalViewMode) => void;
  proposalJsonText: string;
  proposalId: string | null;
  organizePreview: unknown;
  flatCats?: readonly FlatCategoryRow[];
}) {
  if (proposalId === null || proposalOps === null) return null;
  return (
    <Box sx={{ alignSelf: 'flex-start', maxWidth: '100%', width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          Proposed changes
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={proposalViewMode}
          onChange={(_, v: ProposalViewMode | null) => v && onProposalViewMode(v)}
          aria-label="Proposal view"
        >
          <ToggleButton value="summary" aria-label="Summary view">
            <ViewListIcon fontSize="small" sx={{ mr: 0.5 }} />
            Summary
          </ToggleButton>
          <ToggleButton value="json" aria-label="JSON view">
            <DataObjectIcon fontSize="small" sx={{ mr: 0.5 }} />
            JSON
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {proposalViewMode === 'json' ? (
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1.5,
            maxWidth: '100%',
            overflow: 'auto',
            fontSize: 12,
            bgcolor: (t) => (t.palette.mode === 'dark' ? alpha(t.palette.common.black, 0.35) : alpha(t.palette.common.black, 0.06)),
            borderRadius: 1,
            border: (t) => `1px solid ${t.palette.divider}`,
          }}
        >
          {proposalJsonText}
        </Box>
      ) : (
        <Box
          sx={{
            p: 1.5,
            maxWidth: '100%',
            overflow: 'auto',
            bgcolor: (t) => (t.palette.mode === 'dark' ? alpha(t.palette.common.black, 0.35) : alpha(t.palette.common.black, 0.06)),
            borderRadius: 1,
            border: (t) => `1px solid ${t.palette.divider}`,
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1.5, fontWeight: 500 }}>
            {buildProposalOverviewText(proposalOps, flatCats)}
          </Typography>
          <ProposalSummary ops={proposalOps} flatCats={flatCats} />
        </Box>
      )}
      <OrganizePreviewPanel preview={organizePreview} />
      <OutlinePreviewPanel ops={proposalOps} />
    </Box>
  );
}

const Panel = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  right: 0,
  top: 64,
  height: 'calc(100vh - 64px)',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1000,
  boxShadow: theme.shadows[8],
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  borderLeft: `1px solid ${theme.palette.divider}`,
}));

const ResizeHandle = styled('div')(({ theme }) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  bottom: 0,
  width: 4,
  cursor: 'ew-resize',
  zIndex: 1,
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.35),
  },
}));

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 16px',
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.06) : theme.palette.grey[100],
  borderBottom: `1px solid ${theme.palette.divider}`,
  position: 'relative',
  zIndex: 2,
}));

const MessagesContainer = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
});

const Bubble = styled(Paper, {
  shouldForwardProp: (p) => p !== 'role',
})<{ role: 'user' | 'assistant' }>(({ theme, role }) => ({
  padding: theme.spacing(1.5),
  maxWidth: '92%',
  alignSelf: role === 'user' ? 'flex-end' : 'flex-start',
  backgroundColor:
    role === 'user'
      ? theme.palette.primary.main
      : theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.08)
        : theme.palette.grey[100],
  color: role === 'user' ? theme.palette.primary.contrastText : theme.palette.text.primary,
  borderRadius: theme.spacing(2),
  wordBreak: 'break-word',
}));

/** Populated after apply when the backend created or deleted a document; parent may sync tabs/UI. */
export type TreeAssistantClientAction =
  | { type: 'OPEN_DOCUMENT'; documentName: string; categoryId: string }
  | { type: 'CLOSE_DOCUMENT'; documentName: string };

export interface TreeAssistantApplyResult {
  newDocumentName?: string;
  newRootCategoryId?: string;
  deletedDocumentName?: string;
  renamedDocumentOldName?: string;
  renamedDocumentNewName?: string;
  renamedDocumentRootCategoryId?: string;
  /** When present, merged into Edit ▸ Undo as one step (server-built undo/redo payloads). */
  assistantHistory?: { undoSteps: unknown[]; redoSteps: unknown[] } | null;
  /** UI ops in order (open/close tabs); applied by MainLayout after refreshDocuments when server did not persist data. */
  clientActions?: TreeAssistantClientAction[];
}

export interface TreeAssistantWindowProps {
  open: boolean;
  onClose: () => void;
  /** Document name (same as Document.id in the UI), or null if no tab */
  documentId: string | null;
  selectedCategoryId: string | undefined;
  selectedContentIds: string[];
  onApplied: (result?: TreeAssistantApplyResult) => void | Promise<void>;
  /** Library documents (for resolving DUPLICATE_DOCUMENT source + subcase picker). */
  libraryDocuments?: TreeAssistantLibraryDocument[];
  /** Library document names for composer autocomplete (after typing e.g. "document "). */
  documentNamesForAutocomplete?: string[];
  /** Open-document category subtree from Main Layout; flattened for "category "/"cat " autocomplete (shows name · parent when duplicated). */
  openDocumentCategoryTree?: Category[] | null;
  /** Subcase labels for the open document tab (composer autocomplete after "subcase "). */
  documentSubcasesForAutocomplete?: string[];
}

type ChatMessage = { role: 'user' | 'assistant'; content: string };

type TreeAssistantThreadSnapshot = {
  messages: ChatMessage[];
  input: string;
  proposalId: string | null;
  proposalOps: unknown[] | null;
  organizePreview: unknown;
  proposalViewMode: ProposalViewMode;
  panelMode: TreeAssistantPanelMode;
  /** Outline tab: last build / validation message (not mixed into AI chat). */
  outlineFeedback: PanelFeedback | null;
  outlineBulletText: string;
  outlineIncludeCreateDoc: boolean;
  outlineNewDocName: string;
  /** Legacy Import tab fields (pre-merge); restored into outline* on load when present. */
  bulkRowsPasteText?: string;
  bulkRowsFeedback?: PanelFeedback | null;
};

function emptyTreeAssistantThread(): TreeAssistantThreadSnapshot {
  return {
    messages: [],
    input: '',
    proposalId: null,
    proposalOps: null,
    organizePreview: null,
    proposalViewMode: 'summary',
    panelMode: 'assistant',
    outlineFeedback: null,
    outlineBulletText: '',
    outlineIncludeCreateDoc: false,
    outlineNewDocName: '',
    bulkRowsPasteText: '',
    bulkRowsFeedback: null,
  };
}

const TreeAssistantWindow: React.FC<TreeAssistantWindowProps> = ({
  open,
  onClose,
  documentId,
  selectedCategoryId,
  selectedContentIds,
  onApplied,
  libraryDocuments,
  documentNamesForAutocomplete,
  openDocumentCategoryTree,
  documentSubcasesForAutocomplete,
}) => {
  const { getToolbarTooltipTextForState } = useHelpContent();
  const hasOpenDocument = Boolean(documentId?.trim());
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [proposalId, setProposalId] = useState<string | null>(null);
  /** Parsed `operations` from last turn (null = none). */
  const [proposalOps, setProposalOps] = useState<unknown[] | null>(null);
  /** Server-built ORGANIZE layout from last successful turn (same JSON applied on Apply when valid). */
  const [organizePreview, setOrganizePreview] = useState<unknown>(null);
  const [proposalViewMode, setProposalViewMode] = useState<ProposalViewMode>('summary');
  const [panelMode, setPanelMode] = useState<TreeAssistantPanelMode>('assistant');
  const bottomRef = useRef<HTMLDivElement>(null);
  /** In-memory only; each key is documentId or "__no_document__" when no tab is open. */
  const threadsRef = useRef<Record<string, TreeAssistantThreadSnapshot>>({});
  const openDocTrackerRef = useRef<{ open: boolean; key: string } | null>(null);
  const recognitionRef = useRef<WebSpeechRecognition | null>(null);
  const speechBaseRef = useRef('');
  const [isListening, setIsListening] = useState(false);
  const [voiceLang, setVoiceLang] = useState(readInitialTreeAssistantVoiceLang);
  const [panelWidth, setPanelWidth] = useState(readTreeAssistantPanelWidth);
  /** Pasted bullets → client-built APPLY_CATEGORY_OUTLINE proposal. */
  const [outlineBulletText, setOutlineBulletText] = useState('');
  const [outlineIncludeCreateDoc, setOutlineIncludeCreateDoc] = useState(false);
  const [outlineNewDocName, setOutlineNewDocName] = useState('');
  const [outlineFeedback, setOutlineFeedback] = useState<PanelFeedback | null>(null);

  /** Auto-derived document name when the outline has a single root line. */
  const outlineNewDocumentNaming = useMemo(() => {
    const t = outlineBulletText.trim();
    if (!t) return null;
    const parsed = parseUnifiedTreeImport(t);
    if (parsed.kind !== 'tree') return null;
    const resolved = resolveNewDocumentImportNaming(parsed.nodes, outlineNewDocName);
    return resolved.ok && !resolved.explicitDocumentName ? resolved : null;
  }, [outlineBulletText, outlineNewDocName]);

  /** Apply with DUPLICATE_DOCUMENT: pick subcases/groups before propose+apply. */
  const [duplicateSubcasePicker, setDuplicateSubcasePicker] = useState<{
    duplicateIndex: number;
    sourceDocumentName: string;
    newDocumentName: string;
    categoryId: string;
    initialSubcases: string[];
    operationsDraft: unknown[];
  } | null>(null);

  const outlineTextAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const importFileInputRef = useRef<HTMLInputElement | null>(null);

  const runOutlineEditorOp = useCallback(
    (op: 'indent' | 'outdent' | 'bullet') => {
      const el = outlineTextAreaRef.current;
      if (!el || loading) return;
      const { value, selectionStart, selectionEnd } = el;
      const { text } = outlineApplyBlockOp(value, selectionStart, selectionEnd, op);
      setOutlineBulletText(text);
      window.setTimeout(() => {
        const ta = outlineTextAreaRef.current;
        if (!ta) return;
        ta.focus();
        const focusLine = outlineFocusLineForBlockSelection(selectionStart, selectionEnd, value);
        const caret = outlineEndOfLineOffset(text, focusLine);
        ta.setSelectionRange(caret, caret);
      }, 0);
    },
    [loading],
  );

  const runOutlineNewLine = useCallback(
    (mode: 'sibling' | 'child') => {
      const el = outlineTextAreaRef.current;
      if (!el || loading) return;
      const { value, selectionStart } = el;
      const { text, caret } = outlineInsertIndentedNewLine(value, selectionStart, mode);
      setOutlineBulletText(text);
      window.setTimeout(() => {
        const ta = outlineTextAreaRef.current;
        if (!ta) return;
        ta.focus();
        ta.setSelectionRange(caret, caret);
      }, 0);
    },
    [loading],
  );

  const insertOutlineExample = useCallback(() => {
    if (loading) return;
    const sample =
      'Case study\n  Interviews\n    Witness:\n    First answer\n    Second answer\n  Exhibits\n    Exhibit A:\n    Summary line';
    setOutlineBulletText((prev) => (prev.trim() ? `${prev.trim()}\n${sample}` : sample));
    window.setTimeout(() => outlineTextAreaRef.current?.focus(), 0);
  }, [loading]);

  const handleImportLoadFileClick = useCallback(() => {
    if (loading) return;
    importFileInputRef.current?.click();
  }, [loading]);

  const handleImportFileSelected = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target;
      const file = input.files?.[0];
      input.value = '';
      if (!file || loading) return;

      const name = file.name.toLowerCase();
      const allowedExt = name.endsWith('.txt') || name.endsWith('.text') || name.endsWith('.md');
      const isPlainText = !file.type || file.type.startsWith('text/');
      if (!allowedExt && !isPlainText) {
        setOutlineFeedback(
          panelFeedback('Choose a plain text file (.txt, .md, or text/*).', 'error'),
        );
        return;
      }
      if (file.size > IMPORT_TEXT_FILE_MAX_BYTES) {
        setOutlineFeedback(
          panelFeedback(
            `File is too large (max ${Math.round(IMPORT_TEXT_FILE_MAX_BYTES / (1024 * 1024))} MB).`,
            'error',
          ),
        );
        return;
      }

      try {
        const raw = await file.text();
        const text = raw.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        if (!text.trim()) {
          setOutlineFeedback(panelFeedback('File is empty.', 'error'));
          return;
        }
        setOutlineBulletText(text);
        const lineCount = text.split('\n').length;
        setOutlineFeedback(
          panelFeedback(`Loaded ${file.name} (${lineCount} line${lineCount === 1 ? '' : 's'}).`, 'success'),
        );
        window.setTimeout(() => outlineTextAreaRef.current?.focus(), 0);
      } catch (err) {
        console.error(err);
        setOutlineFeedback(
          panelFeedback(
            err instanceof Error ? err.message : 'Could not read the file.',
            'error',
          ),
        );
      }
    },
    [loading],
  );

  const handleOutlineEditorDoubleClick = useCallback((e: React.MouseEvent<HTMLTextAreaElement>) => {
    queueMicrotask(() => outlineCollapseDoubleClickIfBulletPrefix(e.currentTarget));
  }, []);

  const handleOutlineEditorKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (loading) return;
      if (e.nativeEvent.isComposing) return;
      const el = outlineTextAreaRef.current;
      if (!el || e.currentTarget !== el) return;

      if (e.key === 'Tab') {
        e.preventDefault();
        const { value, selectionStart, selectionEnd } = el;
        const op = e.shiftKey ? 'outdent' : 'indent';
        const { text } = outlineApplyBlockOp(value, selectionStart, selectionEnd, op);
        const focusLine = outlineFocusLineForBlockSelection(selectionStart, selectionEnd, value);
        const caret = outlineEndOfLineOffset(text, focusLine);
        setOutlineBulletText(text);
        window.setTimeout(() => {
          const ta = outlineTextAreaRef.current;
          if (!ta) return;
          ta.focus();
          ta.setSelectionRange(caret, caret);
        }, 0);
        return;
      }

      if (e.key !== 'Enter' || e.shiftKey) return;
      if (el.selectionStart !== el.selectionEnd) return;
      const bulletApplied = outlineEnterInsertsSiblingBullet(el.value, el.selectionStart);
      if (bulletApplied) {
        e.preventDefault();
        setOutlineBulletText(bulletApplied.text);
        window.setTimeout(() => {
          const ta = outlineTextAreaRef.current;
          if (!ta) return;
          ta.focus();
          ta.setSelectionRange(bulletApplied.caret, bulletApplied.caret);
        }, 0);
        return;
      }
      const indentApplied = outlineEnterInsertsIndentedContinuation(el.value, el.selectionStart);
      if (indentApplied) {
        e.preventDefault();
        setOutlineBulletText(indentApplied.text);
        window.setTimeout(() => {
          const ta = outlineTextAreaRef.current;
          if (!ta) return;
          ta.focus();
          ta.setSelectionRange(indentApplied.caret, indentApplied.caret);
        }, 0);
      }
    },
    [loading],
  );

  const voiceSupported = useMemo(() => !!getWebSpeechRecognitionCtor(), []);

  const abortVoiceInput = useCallback(() => {
    try {
      recognitionRef.current?.abort();
    } catch {
      /* ignore */
    }
    recognitionRef.current = null;
    setIsListening(false);
  }, []);

  const chatComposerInputRef = useRef<HTMLTextAreaElement | null>(null);

  const documentNamesSorted = useMemo(() => {
    const raw = documentNamesForAutocomplete ?? [];
    return Array.from(new Set(raw.map((s) => String(s).trim()).filter(Boolean))).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' }),
    );
  }, [documentNamesForAutocomplete]);

  const flatCategoriesAutocomplete = useMemo(
    () => flattenCategoriesForAutocomplete(openDocumentCategoryTree ?? undefined),
    [openDocumentCategoryTree],
  );

  const documentSubcasesSorted = useMemo(() => {
    const raw = documentSubcasesForAutocomplete ?? [];
    return Array.from(new Set(raw.map((s) => String(s).trim()).filter(Boolean))).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' }),
    );
  }, [documentSubcasesForAutocomplete]);

  const [composerSuggest, setComposerSuggest] = useState<ComposerSuggestState | null>(null);
  const [composerSuggestAnchorEl, setComposerSuggestAnchorEl] = useState<HTMLElement | null>(null);

  const syncComposerSuggest = useCallback(
    (value: string, cursor: number) => {
      setComposerSuggest(
        buildComposerSuggestState(
          value,
          cursor,
          documentNamesSorted,
          flatCategoriesAutocomplete,
          documentSubcasesSorted,
          loading,
        ),
      );
    },
    [documentNamesSorted, flatCategoriesAutocomplete, documentSubcasesSorted, loading],
  );

  const applyComposerPick = useCallback(
    (pick: ComposerSuggestPick) => {
      setInput((prev) => {
        const rs = Math.min(pick.replaceStart, prev.length);
        const re = Math.min(pick.replaceEnd, prev.length);
        const next = prev.slice(0, rs) + pick.insert + prev.slice(re);
        const pos = rs + pick.insert.length;
        queueMicrotask(() => {
          const ta = chatComposerInputRef.current;
          if (ta) {
            ta.focus();
            try {
              ta.setSelectionRange(pos, pos);
            } catch {
              /* ignore */
            }
          }
          syncComposerSuggest(next, pos);
        });
        return next;
      });
    },
    [syncComposerSuggest],
  );

  const handleComposerCaretOrValue = useCallback(
    (e: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const el = e.currentTarget;
      syncComposerSuggest(el.value, el.selectionStart ?? el.value.length);
    },
    [syncComposerSuggest],
  );

  useEffect(() => {
    if (loading) setComposerSuggest(null);
  }, [loading]);

  useEffect(() => {
    if (!open || panelMode !== 'assistant') setComposerSuggest(null);
  }, [open, panelMode]);

  /** Put a previous user message into the composer so it can be edited and sent again. */
  const handleReuseUserPrompt = useCallback(
    (text: string) => {
      abortVoiceInput();
      setInput(text);
      setComposerSuggest(null);
      queueMicrotask(() => {
        const el = chatComposerInputRef.current;
        if (!el) return;
        el.focus();
        const len = text.length;
        try {
          el.setSelectionRange(len, len);
        } catch {
          /* ignore */
        }
        syncComposerSuggest(text, len);
      });
    },
    [abortVoiceInput, syncComposerSuggest],
  );

  /** Latest thread fields for persist-on-close / document switch (avoid listing them as effect deps → update loops). */
  const threadSnapshotRef = useRef<TreeAssistantThreadSnapshot>(emptyTreeAssistantThread());
  threadSnapshotRef.current = {
    messages,
    input,
    proposalId,
    proposalOps,
    organizePreview,
    proposalViewMode,
    panelMode,
    outlineFeedback,
    outlineBulletText,
    outlineIncludeCreateDoc,
    outlineNewDocName,
  };

  const handleVoiceLangChange = useCallback((event: SelectChangeEvent<string>) => {
    const v = normalizeTreeAssistantSpeechCode(event.target.value);
    setVoiceLang(v);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(TREE_ASSISTANT_SPEECH_LANGUAGE_KEY, v);
    }
    if (recognitionRef.current) {
      recognitionRef.current.lang = v;
    }
  }, []);

  const handlePanelResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startW = panelWidth;
      let lastW = startW;

      const onMove = (moveEvent: MouseEvent) => {
        const nw = Math.min(
          TREE_ASSISTANT_PANEL_MAX_W,
          Math.max(TREE_ASSISTANT_PANEL_MIN_W, startW + (startX - moveEvent.clientX)),
        );
        lastW = nw;
        setPanelWidth(nw);
      };

      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem(TREE_ASSISTANT_PANEL_WIDTH_KEY, String(lastW));
        }
      };

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [panelWidth],
  );

  const toggleVoiceInput = useCallback(() => {
    if (isListening) {
      try {
        recognitionRef.current?.stop();
      } catch {
        /* ignore */
      }
      return;
    }
    if (loading) return;
    const Ctor = getWebSpeechRecognitionCtor();
    if (!Ctor) return;

    speechBaseRef.current = input;
    const recognition = new Ctor();
    recognitionRef.current = recognition;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = voiceLang;

    recognition.onresult = (event: WebSpeechRecognitionEvent) => {
      const { results } = event;
      let finalText = '';
      let interimText = '';
      for (let i = 0; i < results.length; i++) {
        const r = results[i];
        const t = r[0]?.transcript ?? '';
        if (!t) continue;
        if (r.isFinal) finalText += t;
        else interimText += t;
      }
      const spoken = (finalText + interimText).trim();
      const base = speechBaseRef.current;
      const next =
        !spoken ? base : `${base}${base.length > 0 && !/\s$/.test(base) && !/^\s/.test(spoken) ? ' ' : ''}${spoken}`;
      setInput(next);
      queueMicrotask(() => syncComposerSuggest(next, next.length));
    };

    recognition.onerror = (event: WebSpeechRecognitionErrorEvent) => {
      if (event.error === 'aborted') {
        recognitionRef.current = null;
        setIsListening(false);
        return;
      }
      console.error('Structure builder speech:', event.error);
      if (event.error === 'not-allowed') {
        setMessages((m) => [
          ...m,
          {
            role: 'assistant',
            content:
              'Microphone permission was denied. Allow microphone access for voice prompts, or type your message instead.',
          },
        ]);
      }
      try {
        recognition.abort();
      } catch {
        /* ignore */
      }
      recognitionRef.current = null;
      setIsListening(false);
    };

    recognition.onend = () => {
      recognitionRef.current = null;
      setIsListening(false);
    };

    try {
      recognition.start();
      setIsListening(true);
    } catch (e) {
      console.error(e);
      recognitionRef.current = null;
      setIsListening(false);
    }
  }, [isListening, loading, input, voiceLang, syncComposerSuggest]);

  useEffect(
    () => () => {
      try {
        recognitionRef.current?.abort();
      } catch {
        /* ignore */
      }
      recognitionRef.current = null;
    },
    [],
  );

  useEffect(() => {
    if (!open || hasOpenDocument) return;
    setOutlineIncludeCreateDoc(true);
    setPanelMode((mode) => (mode === 'import' ? 'assistant' : mode));
  }, [open, hasOpenDocument]);

  const proposalJsonText = useMemo(
    () => (proposalOps !== null ? JSON.stringify(proposalOps, null, 2) : ''),
    [proposalOps]
  );

  useEffect(() => {
    const key = documentId ?? '__no_document__';
    const prev = openDocTrackerRef.current;

    if (!open) {
      if (prev?.open === true) {
        threadsRef.current[prev.key] = { ...threadSnapshotRef.current };
      }
      openDocTrackerRef.current = { open: false, key };
      setMessages([]);
      setInput('');
      setProposalId(null);
      setProposalOps(null);
      setOrganizePreview(null);
      setProposalViewMode('summary');
      setPanelMode('assistant');
      setOutlineBulletText('');
      setOutlineIncludeCreateDoc(false);
      setOutlineNewDocName('');
      setOutlineFeedback(null);
      setLoading(false);
      setComposerSuggest(null);
      abortVoiceInput();
      return;
    }

    if (prev && prev.open && prev.key === key) {
      openDocTrackerRef.current = { open: true, key };
      return;
    }

    if (prev && prev.open && prev.key !== key) {
      threadsRef.current[prev.key] = { ...threadSnapshotRef.current };
    }

    const loaded = threadsRef.current[key] ?? emptyTreeAssistantThread();
    setMessages(loaded.messages);
    setInput(loaded.input);
    setProposalId(loaded.proposalId);
    setProposalOps(loaded.proposalOps);
    setOrganizePreview(loaded.organizePreview);
    setProposalViewMode(loaded.proposalViewMode);
    setPanelMode(parsePanelMode(loaded.panelMode));
    setOutlineFeedback(normalizeStoredPanelFeedback(loaded.outlineFeedback));
    setOutlineBulletText(loaded.outlineBulletText ?? '');
    setOutlineIncludeCreateDoc(Boolean(loaded.outlineIncludeCreateDoc));
    setOutlineNewDocName(loaded.outlineNewDocName ?? '');
    const legacyBulk = (loaded.bulkRowsPasteText ?? '').trim();
    if (!loaded.outlineBulletText?.trim() && legacyBulk) {
      setOutlineBulletText(legacyBulk);
    }
    if (!normalizeStoredPanelFeedback(loaded.outlineFeedback)) {
      setOutlineFeedback(normalizeStoredPanelFeedback(loaded.bulkRowsFeedback));
    }
    openDocTrackerRef.current = { open: true, key };
    queueMicrotask(() => {
      const text = loaded.input;
      syncComposerSuggest(text, text.length);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- restore chat thread only when panel/tab changes; including syncComposerSuggest would reset state when autocomplete sources refresh
  }, [open, documentId, abortVoiceInput]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading, proposalOps, proposalViewMode, organizePreview, panelMode, outlineFeedback]);

  const handleResetOutlineWorkspace = useCallback(() => {
    setOutlineBulletText('');
    setOutlineIncludeCreateDoc(false);
    setOutlineNewDocName('');
    setOutlineFeedback(null);
    setProposalId(null);
    setProposalOps(null);
    setOrganizePreview(null);
    setProposalViewMode('summary');
  }, []);

  const handleBuildImportFromPaste = async () => {
    if (loading) return;
    const text = outlineBulletText.trim();
    const createDocFirst = outlineIncludeCreateDoc || !hasOpenDocument;

    if (!text) {
      if (!createDocFirst) {
        setOutlineFeedback(
          panelFeedback(
            'Paste an outline, or check “Create empty document first” and enter a document name.',
            'error',
          ),
        );
        return;
      }
      const dn = outlineNewDocName.trim();
      if (!dn) {
        setOutlineFeedback(panelFeedback('Enter a name for the new document.', 'error'));
        return;
      }
      abortVoiceInput();
      setProposalId(null);
      setProposalOps(null);
      setOrganizePreview(null);
      setProposalViewMode('summary');
      setOutlineFeedback(null);
      setLoading(true);
      try {
        const { data } = await api.post('/tree-assistant/propose', {
          documentId: documentId || undefined,
          operations: [{ type: 'CREATE_DOCUMENT', documentName: dn }],
        });
        const assistant = (data.assistantMessage as string) || '';
        const next = proposalUiFromTurnResponse(data);
        setOutlineFeedback(
          panelFeedback(assistant || 'Could not build proposal.', next.proposalId ? 'success' : 'error'),
        );
        setProposalId(next.proposalId);
        setProposalOps(next.proposalOps);
        setOrganizePreview(next.organizePreview);
      } catch (e) {
        console.error(e);
        setOutlineFeedback(panelFeedback(formatTreeAssistantRequestError(e), 'error'));
      } finally {
        setLoading(false);
      }
      return;
    }

    const parsed = parseUnifiedTreeImport(text);
    if (parsed.kind === 'error') {
      setOutlineFeedback(panelFeedback(parsed.error, 'error'));
      return;
    }

    if (parsed.kind === 'contentOnly') {
      const parentId = selectedCategoryId?.trim() || '';
      if (!parentId) {
        setOutlineFeedback(
          panelFeedback(
            'Content-only paste: select a content category in the category structure, or add folder lines above your subcase blocks.',
            'error',
          ),
        );
        return;
      }
      const parentCat = findCategoryByIdInTree(openDocumentCategoryTree ?? null, parentId);
      const dupErr = validateBulkContentLinesUniqueness(parsed.lines, parentCat?.contents);
      if (dupErr) {
        setOutlineFeedback(panelFeedback(dupErr, 'error'));
        return;
      }
      if (!documentId) {
        setOutlineFeedback(panelFeedback('Open a document tab first.', 'error'));
        return;
      }
      abortVoiceInput();
      setProposalId(null);
      setProposalOps(null);
      setOrganizePreview(null);
      setProposalViewMode('summary');
      setOutlineFeedback(null);
      setLoading(true);
      const operations: unknown[] = [{ type: 'BULK_CREATE_CONTENT', parentCategoryId: parentId, lines: parsed.lines }];
      try {
        const { data } = await api.post('/tree-assistant/propose', {
          documentId: documentId || undefined,
          operations,
        });
        const assistant = (data.assistantMessage as string) || '';
        const next = proposalUiFromTurnResponse(data);
        setOutlineFeedback(
          panelFeedback(assistant || 'Could not build proposal.', next.proposalId ? 'success' : 'error'),
        );
        setProposalId(next.proposalId);
        setProposalOps(next.proposalOps);
        setOrganizePreview(next.organizePreview);
      } catch (e) {
        console.error(e);
        setOutlineFeedback(panelFeedback(formatTreeAssistantRequestError(e), 'error'));
      } finally {
        setLoading(false);
      }
      return;
    }

    const nodes = parsed.nodes;
    const operations: unknown[] = [];
    if (createDocFirst) {
      const naming = resolveNewDocumentImportNaming(nodes, outlineNewDocName);
      if (!naming.ok) {
        setOutlineFeedback(panelFeedback(naming.error, 'error'));
        return;
      }
      const { documentName: dn, outlineNodes: rawOutlineNodes, explicitDocumentName: explicitDocName } = naming;
      const outlineNodes = explicitDocName ? rawOutlineNodes : hoistOutlineRootsForNewDocument(rawOutlineNodes);
      const useApplyOnNewRoot =
        outlineNodes.length > 0 && shouldUseApplyOutlineOnNewDocument(rawOutlineNodes, explicitDocName);
      if (outlineNodes.length > 0) {
        const outlineErr = validateOutlineForProposal(outlineNodes, dn);
        if (outlineErr) {
          setOutlineFeedback(panelFeedback(outlineErr, 'error'));
          return;
        }
      }
      if (outlineNodes.length === 0) {
        operations.push({ type: 'CREATE_DOCUMENT', documentName: dn });
      } else if (useApplyOnNewRoot) {
        operations.push({ type: 'CREATE_DOCUMENT', documentName: dn });
        operations.push({ type: 'APPLY_CATEGORY_OUTLINE', parentCategoryId: 'NEW_ROOT', nodes: outlineNodes });
      } else {
        operations.push({ type: 'CREATE_DOCUMENT', documentName: dn, nodes: outlineNodes });
      }
    } else {
      const anchor = selectedCategoryId?.trim() || '';
      if (!anchor) {
        setOutlineFeedback(
          panelFeedback(
            'Click a category in the category structure to choose where the outline attaches, or check “Create empty document first”.',
            'error',
          ),
        );
        return;
      }
      if (!documentId) {
        setOutlineFeedback(
          panelFeedback(
            'Open a document tab first, or use “Create empty document first” with a new document name.',
            'error',
          ),
        );
        return;
      }
      const anchorNode = findCategoryByIdInTree(openDocumentCategoryTree ?? null, anchor);
      const outlineErr = validateOutlineForProposal(nodes, anchorNode?.name);
      if (outlineErr) {
        setOutlineFeedback(panelFeedback(outlineErr, 'error'));
        return;
      }
      const bucketErr = validateOutlineMinTopLevelForContentBucketAnchor(
        nodes,
        isContentCategoryBucketForOutline(anchorNode),
      );
      if (bucketErr) {
        setOutlineFeedback(panelFeedback(bucketErr, 'error'));
        return;
      }
      operations.push({ type: 'APPLY_CATEGORY_OUTLINE', parentCategoryId: anchor, nodes });
    }
    abortVoiceInput();
    setProposalId(null);
    setProposalOps(null);
    setOrganizePreview(null);
    setProposalViewMode('summary');
    setOutlineFeedback(null);
    setLoading(true);
    try {
      const { data } = await api.post('/tree-assistant/propose', {
        documentId: documentId || undefined,
        operations,
      });
      const assistant = (data.assistantMessage as string) || '';
      const next = proposalUiFromTurnResponse(data);
      setOutlineFeedback(
        panelFeedback(
          assistant || 'Could not build proposal.',
          next.proposalId ? 'success' : 'error',
        ),
      );
      setProposalId(next.proposalId);
      setProposalOps(next.proposalOps);
      setOrganizePreview(next.organizePreview);
    } catch (e) {
      console.error(e);
      setOutlineFeedback(panelFeedback(formatTreeAssistantRequestError(e), 'error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;
    abortVoiceInput();
    setInput('');
    setComposerSuggest(null);
    setProposalId(null);
    setProposalOps(null);
    setOrganizePreview(null);
    setProposalViewMode('summary');
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setLoading(true);
    try {
      const { data } = await api.post('/tree-assistant/turn', {
        message: text,
        documentId: documentId || undefined,
        selectedCategoryId: selectedCategoryId || undefined,
        selectedContentIds: selectedContentIds || [],
      });
      const assistant = (data.assistantMessage as string) || '';
      setMessages((m) => [...m, { role: 'assistant', content: assistant }]);
      const next = proposalUiFromTurnResponse(data);
      setProposalId(next.proposalId);
      setProposalOps(next.proposalOps);
      setOrganizePreview(next.organizePreview);
    } catch (e) {
      console.error(e);
      setMessages((m) => [...m, { role: 'assistant', content: formatTreeAssistantRequestError(e) }]);
    } finally {
      setLoading(false);
    }
  };

  /** Enter sends; when suggestions open, arrows move, Enter/Tab accept, Escape closes */
  const handleComposerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const suggestOpen = Boolean(composerSuggest && composerSuggest.picks.length > 0);
    const curSuggest = composerSuggest;
    if (suggestOpen && curSuggest) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setComposerSuggest((s) => {
          if (!s?.picks.length) return s;
          return { ...s, active: (s.active + 1) % s.picks.length };
        });
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setComposerSuggest((s) => {
          if (!s?.picks.length) return s;
          return { ...s, active: (s.active - 1 + s.picks.length) % s.picks.length };
        });
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setComposerSuggest(null);
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        const pick = curSuggest.picks[curSuggest.active];
        if (pick) applyComposerPick(pick);
        return;
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        const pick = curSuggest.picks[curSuggest.active];
        if (pick) applyComposerPick(pick);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  const executeApplyRequest = async (pid: string) => {
    const { data } = await api.post('/tree-assistant/apply', {
      proposalId: pid,
      selectedContentIds: selectedContentIds || [],
    });
    if (data.success) {
      const applyMsg = formatApplySuccessChatMessage(proposalOps, flatCategoriesAutocomplete, data);
      setMessages((m) => [...m, { role: 'assistant', content: applyMsg }]);
      const applySuccessMsg =
        proposalOps && proposalOps.length > 0
          ? proposalOps.length === 1
            ? '1 change applied.'
            : `${proposalOps.length} changes applied.`
          : String(data.message ?? '').trim() || 'Applied successfully.';
      if (panelMode === 'import') {
        setOutlineFeedback(panelFeedback(applySuccessMsg, 'success'));
      }
      setProposalId(null);
      setProposalOps(null);
      setOrganizePreview(null);
      const name = data.newDocumentName as string | undefined | null;
      const rootId = data.newRootCategoryId as string | undefined | null;
      const deleted = data.deletedDocumentName as string | undefined | null;
      const rOld = data.renamedDocumentOldName as string | undefined | null;
      const rNew = data.renamedDocumentNewName as string | undefined | null;
      const rRoot = data.renamedDocumentRootCategoryId as string | undefined | null;
      const undoRaw = data.assistantUndoSteps;
      const redoRaw = data.assistantRedoSteps;
      const undoSteps = Array.isArray(undoRaw) ? undoRaw : [];
      const redoSteps = Array.isArray(redoRaw) ? redoRaw : [];
      const assistantHistory = undoSteps.length > 0 ? { undoSteps, redoSteps } : null;
      const clientActions = parseTreeAssistantClientActions(data.clientActions);
      let applyResult: TreeAssistantApplyResult | undefined;
      if (name && rootId) {
        applyResult = { newDocumentName: name, newRootCategoryId: rootId, assistantHistory, clientActions };
      } else if (deleted && String(deleted).trim()) {
        applyResult = { deletedDocumentName: String(deleted).trim(), clientActions };
      } else if (rOld && String(rOld).trim() && rNew && String(rNew).trim()) {
        applyResult = {
          renamedDocumentOldName: String(rOld).trim(),
          renamedDocumentNewName: String(rNew).trim(),
          renamedDocumentRootCategoryId: rRoot && String(rRoot).trim() ? String(rRoot).trim() : undefined,
          assistantHistory,
          clientActions,
        };
      } else {
        applyResult =
          assistantHistory || clientActions ? { assistantHistory, clientActions } : undefined;
      }
      await onApplied(applyResult);
    } else {
      const failMsg = data.message || 'Apply failed.';
      setMessages((m) => [...m, { role: 'assistant', content: failMsg }]);
      if (panelMode === 'import') {
        setOutlineFeedback(panelFeedback(failMsg, 'error'));
      }
    }
  };

  const handleApply = async () => {
    if (!proposalId || loading) return;
    if (!hasOpenDocument && !isNewDocumentCreationProposal(proposalOps)) {
      const msg =
        'With no document tab open, only proposals that create a new document can be applied. ' +
        'Use Instructions (AI) to ask for a new document (or duplicate from the library), or use Import with “Create empty document first”.';
      setMessages((m) => [...m, { role: 'assistant', content: msg }]);
      if (panelMode === 'import') {
        setOutlineFeedback(panelFeedback(msg, 'error'));
      }
      return;
    }
    const dupIdx = findDuplicateDocumentOpIndex(proposalOps);
    if (dupIdx >= 0 && libraryDocuments?.length) {
      const dup = asOpObject(proposalOps![dupIdx]);
      if (dup) {
        const src = strField(dup, 'sourceDocumentName', 'documentName');
        const newName = strField(dup, 'newDocumentName', 'name');
        const catId = resolveLibraryDocumentCategoryId(src, libraryDocuments);
        if (catId) {
          const raw = dup.subcases;
          const initial: string[] = Array.isArray(raw)
            ? (raw as unknown[]).map((x) => String(x).trim()).filter(Boolean)
            : [];
          const operationsDraft = JSON.parse(JSON.stringify(proposalOps)) as unknown[];
          setDuplicateSubcasePicker({
            duplicateIndex: dupIdx,
            sourceDocumentName: src,
            newDocumentName: newName,
            categoryId: catId,
            initialSubcases: initial,
            operationsDraft,
          });
          return;
        }
      }
    }
    setLoading(true);
    try {
      await executeApplyRequest(proposalId);
    } catch (e) {
      console.error(e);
      const errMsg = formatTreeAssistantRequestError(e);
      setMessages((m) => [...m, { role: 'assistant', content: errMsg }]);
      if (panelMode === 'import') {
        setOutlineFeedback(panelFeedback(errMsg, 'error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateSubcasePickerCancel = () => {
    setDuplicateSubcasePicker(null);
  };

  const handleDuplicateSubcasePickerConfirm = async (subcases: string[]) => {
    const snap = duplicateSubcasePicker;
    if (!snap) return;
    setDuplicateSubcasePicker(null);
    setLoading(true);
    try {
      const ops = JSON.parse(JSON.stringify(snap.operationsDraft)) as Record<string, unknown>[];
      const row = ops[snap.duplicateIndex];
      if (subcases.length === 0) {
        delete row.subcases;
      } else {
        row.subcases = subcases;
      }
      const { data } = await api.post('/tree-assistant/propose', {
        documentId: documentId || undefined,
        operations: ops,
      });
      const next = proposalUiFromTurnResponse(data);
      if (!next.proposalId) {
        const assistant = (data.assistantMessage as string) || '';
        setMessages((m) => [
          ...m,
          { role: 'assistant', content: assistant || 'Could not rebuild proposal after subcase selection.' },
        ]);
        return;
      }
      setProposalId(next.proposalId);
      setProposalOps(next.proposalOps);
      setOrganizePreview(next.organizePreview ?? null);
      setMessages((m) => [
        ...m,
        { role: 'assistant', content: 'Subcase selection saved — applying proposal…' },
      ]);
      await executeApplyRequest(next.proposalId);
    } catch (e) {
      console.error(e);
      const errMsg = formatTreeAssistantRequestError(e);
      setMessages((m) => [...m, { role: 'assistant', content: errMsg }]);
      if (panelMode === 'import') {
        setOutlineFeedback(panelFeedback(errMsg, 'error'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <Panel elevation={4} style={{ width: panelWidth }}>
      <ResizeHandle onMouseDown={handlePanelResizeStart} aria-label="Resize structure builder panel" />
      <Header>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexShrink: 0 }}>
          <AccountTreeIcon color="primary" />
          <Typography variant="subtitle1" fontWeight={600} noWrap>
            Structure builder
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }} />
        <IconButton size="small" onClick={onClose} aria-label="Close structure builder" sx={{ flexShrink: 0 }}>
          <CloseIcon />
        </IconButton>
      </Header>
      <Typography variant="caption" color="primary" sx={{ px: 2, pt: 0.5, display: 'block', fontWeight: 600 }}>
        {documentId ? `Open document: ${documentId}` : 'No document tab'}
      </Typography>
      <Tabs
        value={panelMode}
        onChange={(_, v) => setPanelMode(v as TreeAssistantPanelMode)}
        variant="fullWidth"
        sx={{
          minHeight: 40,
          borderBottom: 1,
          borderColor: 'divider',
          px: 1,
          '& .MuiTab-root': { minHeight: 40, py: 0.5, textTransform: 'none', fontSize: '0.8125rem' },
        }}
      >
        <Tab label="Instructions (AI)" value="assistant" />
        <Tab label="Import" value="import" />
      </Tabs>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {panelMode === 'assistant' ? (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 0.75, display: 'block' }}>
              {hasOpenDocument
                ? 'Describe category structure changes, review the proposal, then Apply. See Help for autocomplete and examples.'
                : 'No document tab open — ask to create a new document or duplicate one from your library, then Apply. Open a document tab for other category structure edits.'}
            </Typography>
            <MessagesContainer>
              {messages.map((msg, i) =>
                msg.role === 'user' ? (
                  <Tooltip
                    key={i}
                    title="Put this prompt in the message box — edit if you like, then send"
                    placement="left"
                  >
                    <Bubble
                      role={msg.role}
                      elevation={0}
                      aria-label="Reuse prompt in message box"
                      tabIndex={0}
                      onClick={() => {
                        if (!loading) handleReuseUserPrompt(msg.content);
                      }}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (loading) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleReuseUserPrompt(msg.content);
                        }
                      }}
                      sx={{
                        cursor: loading ? 'default' : 'pointer',
                        ...(!loading
                          ? {
                              '&:hover': { filter: 'brightness(0.96)' },
                            }
                          : {}),
                        '&:focus-visible': {
                          outline: (t) => `2px solid ${t.palette.primary.main}`,
                          outlineOffset: 2,
                        },
                      }}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {msg.content}
                      </Typography>
                    </Bubble>
                  </Tooltip>
                ) : (
                  <Bubble key={i} role={msg.role} elevation={0}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {msg.content}
                    </Typography>
                  </Bubble>
                ),
              )}
              <TreeAssistantProposalSection
                proposalOps={proposalOps}
                proposalViewMode={proposalViewMode}
                onProposalViewMode={setProposalViewMode}
                proposalJsonText={proposalJsonText}
                proposalId={proposalId}
                organizePreview={organizePreview}
                flatCats={flatCategoriesAutocomplete}
              />
              {loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, alignSelf: 'flex-start' }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">Working…</Typography>
                </Box>
              )}
              <div ref={bottomRef} />
            </MessagesContainer>
          </>
        ) : (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 0.75, display: 'block' }}>
              {hasOpenDocument
                ? 'Paste or load a .txt/.md file: folders (indent) and/or subcase blocks under leaves — one proposal, one Apply. Content-only needs a selected content category.'
                : 'Paste or load a .txt/.md file to create a document and category structure; use "Subcase:" under folder lines for content rows.'}
            </Typography>
            <MessagesContainer>
              {outlineFeedback ? (
                <Alert
                  severity={outlineFeedback.severity}
                  sx={{ mb: 2, whiteSpace: 'pre-wrap' }}
                  onClose={() => setOutlineFeedback(null)}
                >
                  {outlineFeedback.message}
                </Alert>
              ) : null}
              <Box sx={{ mb: 1 }}>
                <Stack
                  direction="row"
                  spacing={0.25}
                  flexWrap="wrap"
                  alignItems="center"
                  sx={{
                    mb: 0.75,
                    gap: 0.25,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    px: 0.5,
                    py: 0.25,
                    bgcolor: 'action.hover',
                  }}
                >
                  <Tooltip
                    title="Indent — add 2 spaces (Tab). Caret jumps to the end of the focused line. Shift+Tab outdents."
                    placement="top"
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={loading}
                        onClick={() => runOutlineEditorOp('indent')}
                        aria-label="Indent outline lines"
                      >
                        <FormatIndentIncreaseIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title="Outdent — remove 2 spaces or one tab (Shift+Tab). Caret jumps to the end of the focused line."
                    placement="top"
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={loading}
                        onClick={() => runOutlineEditorOp('outdent')}
                        aria-label="Outdent outline lines"
                      >
                        <FormatIndentDecreaseIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      'Toggle "- " after indent: adds on plain lines; removes leading * or - style bullets when present.'
                    }
                    placement="top"
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={loading}
                        onClick={() => runOutlineEditorOp('bullet')}
                        aria-label="Toggle dash bullets on outline lines"
                      >
                        <FormatListBulletedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      'Insert newline with the same indentation as the current line. If this line has a dash-style bullet, the new line gets "- " too. Enter at line end does the same.'
                    }
                    placement="top"
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={loading}
                        onClick={() => runOutlineNewLine('sibling')}
                        aria-label="New outline line same level"
                      >
                        <KeyboardReturnIcon fontSize="small" sx={{ transform: 'rotate(-90deg)' }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      'Insert newline one level deeper. If the current line has a dash-style bullet, the new line gets "- " at that depth too.'
                    }
                    placement="top"
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={loading}
                        onClick={() => runOutlineNewLine('child')}
                        aria-label="New outline line deeper"
                      >
                        <SubdirectoryArrowRightIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Append a small sample outline you can edit" placement="top">
                    <span>
                      <Button size="small" variant="text" disabled={loading} onClick={insertOutlineExample} sx={{ minWidth: 'auto', px: 1 }}>
                        Example
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Load import text from a .txt or .md file (replaces the editor)" placement="top">
                    <span>
                      <Button
                        size="small"
                        variant="text"
                        disabled={loading}
                        onClick={handleImportLoadFileClick}
                        startIcon={<UploadFileIcon fontSize="small" />}
                        sx={{ minWidth: 'auto', px: 1 }}
                      >
                        Load file
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
                <input
                  ref={importFileInputRef}
                  type="file"
                  accept=".txt,.text,.md,text/plain"
                  hidden
                  aria-hidden
                  tabIndex={-1}
                  onChange={(ev) => void handleImportFileSelected(ev)}
                />
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  size="small"
                  placeholder={
                    'e.g.\ntest\n  test1\n    subcase1:\n    answer one\n    answer two\n  test2\n    subcase2:\n    answer three\n\nOr content-only:\nWitness:\nFirst line'
                  }
                  value={outlineBulletText}
                  onChange={(e) => setOutlineBulletText(e.target.value)}
                  disabled={loading}
                  inputRef={outlineTextAreaRef}
                  inputProps={{ onKeyDown: handleOutlineEditorKeyDown, onDoubleClick: handleOutlineEditorDoubleClick }}
                  sx={{ mb: 1 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={outlineIncludeCreateDoc || !hasOpenDocument}
                      onChange={(_, c) => setOutlineIncludeCreateDoc(c)}
                      disabled={loading || !hasOpenDocument}
                      size="small"
                    />
                  }
                  label="Create empty document first, then outline on its root"
                  sx={{ alignItems: 'flex-start', mb: outlineIncludeCreateDoc || !hasOpenDocument ? 1 : 0 }}
                />
                {outlineIncludeCreateDoc || !hasOpenDocument ? (
                  outlineNewDocumentNaming ? (
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Document name: <strong>{outlineNewDocumentNaming.documentName}</strong>
                    </Typography>
                  ) : (
                    <TextField
                      fullWidth
                      size="small"
                      label="New document name"
                      helperText="Required when there is no outline, or when the outline has no root element (multiple top-level lines)."
                      value={outlineNewDocName}
                      onChange={(e) => setOutlineNewDocName(e.target.value)}
                      disabled={loading}
                      sx={{ mb: 1 }}
                    />
                  )
                ) : null}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => void handleBuildImportFromPaste()}
                  disabled={
                    loading ||
                    (!outlineBulletText.trim() &&
                      !((outlineIncludeCreateDoc || !hasOpenDocument) && outlineNewDocName.trim()))
                  }
                >
                  Build proposal from paste
                </Button>
              </Box>
              <TreeAssistantProposalSection
                proposalOps={proposalOps}
                proposalViewMode={proposalViewMode}
                onProposalViewMode={setProposalViewMode}
                proposalJsonText={proposalJsonText}
                proposalId={proposalId}
                organizePreview={organizePreview}
                flatCats={flatCategoriesAutocomplete}
              />
              {loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, alignSelf: 'flex-start' }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">Working…</Typography>
                </Box>
              )}
              <div ref={bottomRef} />
            </MessagesContainer>
          </>
        )}
      </Box>
      <Box sx={{ px: 2, pb: 1, display: 'flex', gap: 1, flexWrap: 'wrap', borderTop: 1, borderColor: 'divider' }}>
        <Button variant="contained" disabled={!proposalId || loading} onClick={handleApply}>
          {proposalOps && proposalOps.length > 0
            ? proposalOps.length === 1
              ? 'Apply 1 change'
              : `Apply ${proposalOps.length} changes`
            : 'Apply changes'}
        </Button>
        {panelMode === 'assistant' ? (
          <Button
            variant="outlined"
            disabled={loading}
            onClick={() => {
              const k = documentId ?? '__no_document__';
              threadsRef.current[k] = emptyTreeAssistantThread();
              setMessages([]);
              setInput('');
              setComposerSuggest(null);
              setProposalId(null);
              setProposalOps(null);
              setOrganizePreview(null);
              setProposalViewMode('summary');
              setPanelMode('assistant');
              setOutlineBulletText('');
              setOutlineIncludeCreateDoc(false);
              setOutlineNewDocName('');
              setOutlineFeedback(null);
              setDuplicateSubcasePicker(null);
            }}
          >
            Clear chat
          </Button>
        ) : (
          <Button variant="outlined" disabled={loading} onClick={handleResetOutlineWorkspace}>
            Reset import
          </Button>
        )}
      </Box>
      {panelMode === 'assistant' ? (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'stretch',
            p: 2,
            pt: 1,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <ClickAwayListener onClickAway={() => setComposerSuggest(null)}>
            <Box
              ref={setComposerSuggestAnchorEl}
              sx={{ flex: 1, minWidth: 0, display: 'flex' }}
            >
              <Tooltip
                title={getToolbarTooltipTextForState('tree-assistant-input', true)}
                placement="top"
                sx={{ flex: 1, display: 'flex', minWidth: 0, width: '100%' }}
              >
                <TextField
                  fullWidth
                  multiline
                  maxRows={5}
                  size="small"
                  sx={aiComposerTextFieldSx}
                  inputRef={chatComposerInputRef}
                  placeholder="e.g. Create a document named Interview Wave 2 — or with a tab open: add categories under the root"
                  value={input}
                  inputProps={{
                    onClick: handleComposerCaretOrValue,
                    onSelect: handleComposerCaretOrValue,
                    onKeyUp: handleComposerCaretOrValue,
                  }}
                  onChange={(e) => {
                    const v = e.target.value;
                    setInput(v);
                    const pos = e.target.selectionStart ?? v.length;
                    syncComposerSuggest(v, pos);
                  }}
                  onKeyDown={handleComposerKeyDown}
                />
              </Tooltip>
              <Popper
                open={Boolean(composerSuggest && composerSuggest.picks.length > 0)}
                anchorEl={composerSuggestAnchorEl}
                placement="top-start"
                disablePortal
                style={{ zIndex: 1400 }}
                transition
              >
                {({ TransitionProps }) => (
                  <Grow {...TransitionProps} style={{ transformOrigin: 'center bottom' }}>
                    <Paper
                      variant="outlined"
                      elevation={8}
                      sx={{
                        mt: -0.5,
                        maxHeight: 280,
                        overflow: 'auto',
                        minWidth: 280,
                        maxWidth: 480,
                      }}
                    >
                      <List dense disablePadding aria-label="Message autocomplete suggestions">
                        {composerSuggest?.picks.map((pick, idx) => (
                          <ListItemButton
                            key={`${pick.label}-${pick.insert}-${idx}`}
                            selected={idx === composerSuggest!.active}
                            onMouseDown={(ev) => {
                              ev.preventDefault();
                            }}
                            onClick={() => applyComposerPick(pick)}
                          >
                            <ListItemText
                              primary={pick.label}
                              secondary={
                                pick.insert !== pick.label
                                  ? pick.insert
                                  : undefined
                              }
                              secondaryTypographyProps={{
                                sx: {
                                  fontFamily: 'monospace',
                                  fontSize: '0.72rem',
                                  display: 'block',
                                  wordBreak: 'break-all',
                                  opacity: 0.85,
                                },
                              }}
                            />
                          </ListItemButton>
                        ))}
                      </List>
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </Box>
          </ClickAwayListener>
          <Paper variant="outlined" sx={aiComposerVoicePaperSx}>
            <Tooltip
              title={
                voiceSupported
                  ? getToolbarTooltipTextForState('tree-assistant-voice', true)
                  : 'Voice input is not supported in this browser. Try Chrome or Edge, or type your message.'
              }
              placement="top"
            >
              <span>
                <IconButton
                  color={isListening ? 'error' : 'default'}
                  aria-label={isListening ? 'Stop voice input' : 'Start voice input'}
                  aria-pressed={isListening}
                  onClick={() => toggleVoiceInput()}
                  disabled={!voiceSupported || loading}
                  sx={{ alignSelf: 'center', p: 1.25 }}
                >
                  <MicIcon fontSize="medium" />
                </IconButton>
              </span>
            </Tooltip>
            <DropdownFieldTooltip
              title={getToolbarTooltipTextForState('tree-assistant-voice-language', true)}
            >
              <FormControl size="small" sx={{ minWidth: 68, width: '100%' }}>
                <Select
                  value={voiceLang}
                  onChange={handleVoiceLangChange}
                  disabled={loading || !voiceSupported}
                  aria-label="Voice recognition language"
                  variant="outlined"
                  fullWidth
                  renderValue={(code) => treeAssistantSpeechLangOption(code).short}
                  sx={aiComposerLangSelectSx}
                >
                  {TREE_ASSISTANT_SPEECH_LANG_OPTIONS.map((l) => (
                    <MenuItem key={l.code} value={l.code}>
                      {l.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </DropdownFieldTooltip>
          </Paper>
          <Tooltip title={getToolbarTooltipTextForState('tree-assistant-send', true)} placement="top">
            <span style={{ alignSelf: 'flex-start' }}>
              <IconButton color="primary" onClick={() => void handleSend()} disabled={!input.trim() || loading}>
                <SendIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ) : null}
      {duplicateSubcasePicker ? (
        <DuplicateSubcasePickerDialog
          open
          sourceDocumentName={duplicateSubcasePicker.sourceDocumentName}
          newDocumentName={duplicateSubcasePicker.newDocumentName}
          categoryId={duplicateSubcasePicker.categoryId}
          initialSubcases={duplicateSubcasePicker.initialSubcases}
          onCancel={handleDuplicateSubcasePickerCancel}
          onConfirm={(subcases) => void handleDuplicateSubcasePickerConfirm(subcases)}
        />
      ) : null}
    </Panel>
  );
};

export default TreeAssistantWindow;
